import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Link,
  Snackbar,
  Tooltip as MuiTooltip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
  LinearProgress,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  AccountBalance as AccountBalanceIcon,
  HomeWork as HomeWorkIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Sync as SyncIcon,
  ExpandMore as ExpandMoreIcon,
  NotificationsActive as NotificationsActiveIcon,
  WarningAmber as WarningAmberIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutline as ErrorOutlineIcon,
  FlightTakeoff as FlightTakeoffIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../contexts/ApiContext';
import {
  buildDashboardReminders,
  DASHBOARD_REMINDERS_CHANGED_EVENT,
  POST_DEPLOY_MIGRATIONS_REMINDER_DISMISS_KEY,
  type DashboardReminder,
  type ManualReviewSummary,
  type SupplierVatPolicyRow,
} from '../utils/dashboardReminders';
import {
  formatFlightDisplay,
  formatFlightStatusDisplay,
  flightStatusChipColor,
  type BookingFlightDetails,
} from '../utils/bookingFlightDetails';
import { getUserUiState, putUserUiState } from '../utils/userUiStateApi';
import RecurringTasksCard from '../components/RecurringTasksCard';
import CollapsiblePanel from '../components/CollapsiblePanel';
import {
  formatPersonalSpendPct,
  personalCashflowBarPath,
  personalCashflowBarSize,
  personalCashflowChartHistory,
  personalCashflowChartRows,
  personalCashflowXAxisInterval,
  personalMixRows,
  personalMonthFigurePath,
  personalSpendMonthsNewestFirst,
  personalSpendPartPath,
  PERSONAL_MIX_COLORS,
  type PersonalDashboardHousehold,
  type PersonalDashboardHouseholdSummary,
  type PersonalDashboardMonth,
} from '../utils/personalAccountsDashboard';
import { CASHFLOW_EXPENSES_FILL, CASHFLOW_INCOME_FILL } from '../utils/cashflowColors';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  fitTruncateSx,
} from '../utils/fitTable';
import { effectiveMerchantName } from '../utils/effectiveMerchant';
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  Cell,
  Line,
  ReferenceLine,
} from 'recharts';

interface CronConfig {
  last_run_at: string | null;
  last_run_status: string | null;
  last_run_message: string | null;
  last_run_details: {
    backup?: CronBackupDetails;
    revolut?: {
      transactions?: Record<string, number | string | { count?: number; updated_count?: number; items?: Array<Record<string, any>>; updated_items?: Array<Record<string, any>> }>;
      expenses?: Record<string, number | string | { count?: number; updated_count?: number; items?: Array<Record<string, any>>; updated_items?: Array<Record<string, any>> }>;
      success?: boolean;
      error?: string;
    };
    bookings?: {
      synced_count?: number;
      processed_count?: number;
      updated_count?: number;
      unchanged_count?: number;
      property_breakdown?: Record<string, { processed?: number; created?: number; updated?: number; unchanged?: number }>;
      errors?: string[];
      success?: boolean;
      error?: string;
      items?: Array<Record<string, any>>;
    };
    performance?: CronPerformanceDetails;
    email_report?: CronEmailReport;
    // E2E-only summary fields (also present on recent_runs[].details)
    run_kind?: string;
    passed?: number;
    failed?: number;
    skipped?: number;
    tests_total?: number;
    workflow_run_url?: string;
    git_ref?: string;
    failure_snippet?: string;
  } | null;
  recent_runs?: Array<{
    id: number;
    run_at: string | null;
    status: string;
    message: string | null;
    details: {
      backup?: CronBackupDetails;
      revolut?: {
        transactions?: Record<string, number | { count?: number; updated_count?: number; items?: Array<Record<string, any>>; updated_items?: Array<Record<string, any>> } | CronAccountSyncDetails>;
        expenses?: Record<string, number | { count?: number; updated_count?: number; items?: Array<Record<string, any>>; updated_items?: Array<Record<string, any>> } | CronAccountSyncDetails>;
        success?: boolean;
        error?: string;
      };
      bookings?: {
        synced_count?: number;
        processed_count?: number;
        updated_count?: number;
        unchanged_count?: number;
        property_breakdown?: Record<string, { processed?: number; created?: number; updated?: number; unchanged?: number }>;
        errors?: string[];
        success?: boolean;
        error?: string;
        items?: Array<Record<string, any>>;
      };
      performance?: CronPerformanceDetails;
      email_report?: CronEmailReport;
      run_kind?: string;
      passed?: number;
      failed?: number;
      skipped?: number;
      tests_total?: number;
      workflow_run_url?: string;
      git_ref?: string;
      failure_snippet?: string;
    } | null;
  }>;
}

interface CronBackupDetails {
  success?: boolean;
  status?: string;
  error?: string;
  database_type?: string;
  file?: string;
  created_at?: string;
  size_bytes?: number;
  deleted_old_backups?: number;
  remote_upload?: {
    success?: boolean;
    enabled?: boolean;
    status?: string;
    provider?: string;
    path?: string;
    dropbox_path?: string;
    url?: string;
    sha256?: string;
    error?: string;
    reason?: string;
    destinations?: Array<{
      name?: string;
      success?: boolean;
      provider?: string;
      path?: string;
      overwrite?: boolean;
      error?: string;
      url?: string;
    }>;
  };
}

interface CronAccountSyncDetails {
  count?: number;
  items?: Array<Record<string, any>>;
  attempts?: number;
  error?: string | null;
}

interface CronEmailReport {
  sent?: boolean;
  skipped?: boolean;
  reason?: string;
  error?: string;
  to?: string;
  subject?: string;
}

interface CronMemorySample {
  label?: string;
  captured_at?: string;
  rss_bytes?: number | null;
  max_rss_bytes?: number | null;
  cgroup_current_bytes?: number | null;
  cgroup_peak_bytes?: number | null;
}

interface CronPerformanceDetails {
  process?: {
    pid?: number;
    hostname?: string;
  };
  timings_ms?: {
    total?: number;
    backup?: number;
    revolut?: number;
    bookings?: number;
  };
  memory?: {
    start_rss_bytes?: number | null;
    end_rss_bytes?: number | null;
    rss_delta_bytes?: number | null;
    peak_observed_bytes?: number | null;
    peak_cgroup_bytes?: number | null;
    peak_max_rss_bytes?: number | null;
    samples?: CronMemorySample[];
  };
  restart_signals?: {
    previous_run_at?: string | null;
    previous_pid?: number | null;
    current_pid?: number | null;
    pid_changed_since_last_run?: boolean | null;
  };
}

interface Account {
  id: number;
  account_name: string;
  current_balance?: number;
}

interface Loan {
  id: number;
  loan_name: string;
  loan_type: string;
  current_balance?: number;
  property_id?: number | null;
  property_ids?: number[];
  properties?: Array<{ id: number }>;
}

interface LoanBalanceEntry {
  loan_id?: number | null;
  balance?: number | null;
  date_entered?: string | null;
}

interface Property {
  id: number;
  nickname: string;
  valuation: number;
  reinvented_recruitment_ltd_ownership?: number;
  secodwom_uk_ltd_ownership?: number;
  omhe_props_ltd_ownership?: number;
  omar_ownership?: number;
  heidi_ownership?: number;
  sean_ownership?: number;
  dwayne_ownership?: number;
  lena_ownership?: number;
  coral_ownership?: number;
}

interface RevolutTransaction {
  id: string;
  account_source?: string;
  balance?: number | null;
  amount?: number | null;
  amount_signed?: number | null;
  completed_at?: string | null;
  created_at_revolut?: string | null;
  created_at?: string | null;
}

interface ManualBalanceEntry {
  id: number;
  account_id?: number | null;
  loan_id?: number | null;
  balance: number;
  currency?: string | null;
  date_entered?: string | null;
  account_name?: string;
  loan_name?: string;
}

/** Sean’s home card: Revolut ledger Rev Biz Ire/UK only — exclude manual-balance rows (e.g. names containing “manual”). */
function filterAccountsForSeanDashboard(accounts: Account[]): Account[] {
  return accounts.filter((a) => {
    const n = (a.account_name || '').toLowerCase();
    if (n.includes('manual')) return false;
    const ire =
      (n.includes('rev biz') && /ire/.test(n) && !/\brev biz\s+uk\b/.test(n)) ||
      (n.includes('migrated') && n.includes('ire'));
    const uk =
      (n.includes('rev biz') && /\buk\b/.test(n)) || (n.includes('migrated') && /\buk\b/.test(n));
    return ire || uk;
  });
}

/** Only IRE/UK synced Revolut rows (balance lines for those two accounts). */
function filterRevolutTransactionsForSeanDashboard(txs: RevolutTransaction[]): RevolutTransaction[] {
  return txs.filter((tx) => {
    const s = (tx.account_source || '').toLowerCase();
    return s === 'ireland' || s === 'uk';
  });
}

/** Equity owner columns left-to-right; narrow viewports hide from the right. */
const EQUITY_COL_ORDER = ['omhe', 'rrltd', 'secodwom', 'omar', 'heidi', 'dwayne', 'lena', 'sean', 'coral'] as const;
type EquityColKey = (typeof EQUITY_COL_ORDER)[number];

const EQUITY_COL_LABELS: Record<EquityColKey, string> = {
  omhe: 'OmHe Props',
  rrltd: 'RRLtd',
  secodwom: 'SECODWOM UK Ltd',
  omar: 'Omar',
  heidi: 'Heidi',
  dwayne: 'Dwayne',
  lena: 'Lena',
  sean: 'Sean',
  coral: 'Coral'
};

/** Must match backend `CRON_STATUS_RECENT_RUNS_LIMIT` (GET /api/cron/status). */
const CRON_JOBS_PANEL_MAX = 3;

interface AssetEquityRow {
  omheEquity: number;
  omhePct: number;
  rrLtdEquity: number;
  rrLtdPct: number;
  secodwomEquity: number;
  secodwomPct: number;
  omarEquity: number;
  omarPct: number;
  heidiEquity: number;
  heidiPct: number;
  dwayneEquity: number;
  dwaynePct: number;
  lenaEquity: number;
  lenaPct: number;
  seanEquity: number;
  seanPct: number;
  coralEquity: number;
  coralPct: number;
}

function equitySliceForDataRow(row: AssetEquityRow, key: EquityColKey): { eur: number; pct: number } {
  switch (key) {
    case 'omhe':
      return { eur: row.omheEquity, pct: row.omhePct };
    case 'rrltd':
      return { eur: row.rrLtdEquity, pct: row.rrLtdPct };
    case 'secodwom':
      return { eur: row.secodwomEquity, pct: row.secodwomPct };
    case 'omar':
      return { eur: row.omarEquity, pct: row.omarPct };
    case 'heidi':
      return { eur: row.heidiEquity, pct: row.heidiPct };
    case 'dwayne':
      return { eur: row.dwayneEquity, pct: row.dwaynePct };
    case 'lena':
      return { eur: row.lenaEquity, pct: row.lenaPct };
    case 'sean':
      return { eur: row.seanEquity, pct: row.seanPct };
    case 'coral':
      return { eur: row.coralEquity, pct: row.coralPct };
    default:
      return { eur: 0, pct: 0 };
  }
}

interface EquityTotalsSlice {
  omheEquity: number;
  rrLtdEquity: number;
  secodwomEquity: number;
  omarEquity: number;
  heidiEquity: number;
  dwayneEquity: number;
  lenaEquity: number;
  seanEquity: number;
  coralEquity: number;
}

function equityAmountForTotalRow(totals: EquityTotalsSlice, key: EquityColKey): number {
  switch (key) {
    case 'omhe':
      return totals.omheEquity;
    case 'rrltd':
      return totals.rrLtdEquity;
    case 'secodwom':
      return totals.secodwomEquity;
    case 'omar':
      return totals.omarEquity;
    case 'heidi':
      return totals.heidiEquity;
    case 'dwayne':
      return totals.dwayneEquity;
    case 'lena':
      return totals.lenaEquity;
    case 'sean':
      return totals.seanEquity;
    case 'coral':
      return totals.coralEquity;
    default:
      return 0;
  }
}

type CronHealthState = 'good' | 'warning' | 'critical';

function CronRunSummaryRow({
  failed,
  text,
  state,
}: {
  failed: boolean;
  text: string;
  state?: CronHealthState;
}) {
  const resolvedState: CronHealthState | null = state || (failed ? 'critical' : null);
  const stateColor =
    resolvedState === 'good'
      ? 'success.main'
      : resolvedState === 'warning'
        ? 'warning.main'
        : resolvedState === 'critical'
          ? 'error.main'
          : 'text.secondary';
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, width: '100%', minWidth: 0 }}>
      {resolvedState === 'good' ? (
        <CheckCircleOutlineIcon sx={{ fontSize: '1.125rem', color: stateColor, flexShrink: 0, mt: '2px' }} aria-hidden />
      ) : resolvedState === 'warning' ? (
        <WarningAmberIcon sx={{ fontSize: '1.125rem', color: stateColor, flexShrink: 0, mt: '2px' }} aria-hidden />
      ) : resolvedState === 'critical' ? (
        <ErrorOutlineIcon sx={{ fontSize: '1.125rem', color: stateColor, flexShrink: 0, mt: '2px' }} aria-hidden />
      ) : null}
      <Typography
        component="span"
        variant="body2"
        color={resolvedState ? stateColor : failed ? 'error.main' : 'text.secondary'}
        sx={{ lineHeight: 1.35, wordBreak: 'break-word' }}
      >
        {text}
      </Typography>
    </Box>
  );
}

interface UpcomingBookingRow {
  id: number;
  guest_name?: string | null;
  property_nickname?: string | null;
  source?: string | null;
  status?: string | null;
  check_in_date: string;
  check_out_date: string;
  adults_count?: number | null;
  children_count?: number | null;
  total_payout_amount?: number | string | null;
  host_payout_amount?: number | string | null;
  currency?: string | null;
  reservation_url?: string | null;
  external_booking_id?: string | null;
  merged_booking_ids?: number[] | null;
  flight_in?: BookingFlightDetails | null;
  flight_out?: BookingFlightDetails | null;
  flight_in_display?: string | null;
  flight_out_display?: string | null;
  flight_in_status_display?: string | null;
  flight_out_status_display?: string | null;
}

interface MonthlyStatValue {
  booked_nights: number;
  days_in_month: number;
  occupancy_pct: number;
  avg_price_night: number | null;
  nights_with_payout: number;
}

interface MonthlyStatProperty {
  property_id: number;
  nickname: string;
  current: MonthlyStatValue;
  baseline: {
    occupancy_pct: number | null;
    avg_price_night: number | null;
    years_used: number[];
    years_count: number;
    price_years_count: number;
  };
  prior_years: Array<{ year: number } & MonthlyStatValue>;
  series: Array<{ year: number; month: number; label: string } & MonthlyStatValue>;
  delta: {
    occupancy_pct: number | null;
    occupancy_pct_rel: number | null;
    avg_price_night: number | null;
    avg_price_night_rel: number | null;
  };
}

interface MonthlyBookingStats {
  year: number;
  month: number;
  currency: string;
  properties: MonthlyStatProperty[];
}

function renderFlightCell(
  flight: BookingFlightDetails | null | undefined,
  displayText?: string | null,
  statusText?: string | null
) {
  const details = formatFlightDisplay(flight, displayText);
  const status = formatFlightStatusDisplay(flight, statusText);
  if (details === 'Flight details unknown') {
    return (
      <Typography variant="body2" color="text.secondary">
        {details}
      </Typography>
    );
  }
  return (
    <Box>
      <Typography variant="body2">{details}</Typography>
      {status ? (
        <Chip
          size="small"
          label={status}
          color={flightStatusChipColor(status)}
          sx={{ mt: 0.5 }}
        />
      ) : null}
    </Box>
  );
}

function parseBookingIsoDate(iso: string): Date {
  return new Date(`${iso.slice(0, 10)}T12:00:00`);
}

function getCalendarWeekBounds(reference = new Date()) {
  const today = new Date(reference);
  today.setHours(0, 0, 0, 0);
  const day = today.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() + diffToMonday);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  weekEnd.setHours(23, 59, 59, 999);
  return { today, weekStart, weekEnd };
}

function isCurrentStay(booking: UpcomingBookingRow, today: Date): boolean {
  const checkIn = parseBookingIsoDate(booking.check_in_date);
  const checkOut = parseBookingIsoDate(booking.check_out_date);
  return checkIn <= today && checkOut > today;
}

function isUpcomingStayThisWeek(booking: UpcomingBookingRow, today: Date, weekEnd: Date): boolean {
  const checkIn = parseBookingIsoDate(booking.check_in_date);
  return checkIn > today && checkIn <= weekEnd;
}

function bookingsTabForSource(source?: string | null): 'ical' | 'manual' {
  return source === 'iCal' ? 'ical' : 'manual';
}

function dateToLocalIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function PersonalCashflowTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: PersonalDashboardMonth }>;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const eur = (value: number) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
  const mix = personalMixRows(row.spend);
  return (
    <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', p: 1, borderRadius: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
        {row.label}
      </Typography>
      <Typography variant="caption" display="block">Income {eur(row.income)}</Typography>
      <Typography variant="caption" display="block">Expenses {eur(row.expenses)}</Typography>
      <Typography variant="caption" display="block" sx={{ fontWeight: 700 }}>
        Net {eur(row.net)}
      </Typography>
      {mix.map((item) => (
        <Typography key={item.pillar} variant="caption" display="block">
          {item.label} {formatPersonalSpendPct(item.pct)}
        </Typography>
      ))}
    </Box>
  );
}

const PERSONAL_VIEW_BUTTON_SX = { minWidth: 0, px: 1, py: 0, lineHeight: 1.4 } as const;
const PERSONAL_CASHFLOW_NET_STROKE = '#1d4ed8';
const PERSONAL_CASHFLOW_LEGEND = [
  { name: 'Income', color: CASHFLOW_INCOME_FILL, kind: 'swatch' as const },
  { name: 'Expenses', color: CASHFLOW_EXPENSES_FILL, kind: 'swatch' as const },
  { name: 'Net', color: PERSONAL_CASHFLOW_NET_STROKE, kind: 'line' as const },
];

function PersonalAmountView({
  formatted,
  to,
  ariaLabel,
  onView,
  sx,
}: {
  formatted: string;
  to: string;
  ariaLabel: string;
  onView: (path: string) => void;
  sx?: object;
}) {
  return (
    <TableCell align="right" sx={sx}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
        {formatted}
        <Button size="small" aria-label={ariaLabel} onClick={() => onView(to)} sx={PERSONAL_VIEW_BUTTON_SX}>
          View
        </Button>
      </Box>
    </TableCell>
  );
}

function PersonalCashflowHistory({
  months,
  household,
  formatCurrency,
  onView,
}: {
  months: PersonalDashboardMonth[];
  household: PersonalDashboardHousehold;
  formatCurrency: (value: number) => string;
  onView: (path: string) => void;
}) {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  const chartMonths = useMemo(
    () => personalCashflowChartHistory(months, viewportWidth),
    [months, viewportWidth]
  );
  const chartWidth = Math.max(viewportWidth, 1);
  const chartRows = useMemo(() => personalCashflowChartRows(chartMonths), [chartMonths]);
  const barSize = personalCashflowBarSize(chartMonths.length, chartWidth);
  const xInterval = personalCashflowXAxisInterval(chartMonths.length, chartWidth);

  useLayoutEffect(() => {
    const node = chartRef.current;
    if (!node) return undefined;
    const apply = () => setViewportWidth(node.clientWidth);
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(node);
    return () => observer.disconnect();
  }, [months.length]);

  if (months.length === 0) return null;

  return (
    <>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        As many months as fit this page. Click a bar to open that month.
      </Typography>
      <Box sx={{ position: 'relative', mb: 1 }}>
        <Box
          data-testid="personal-cashflow-legend"
          sx={{
            position: 'absolute',
            top: 4,
            right: 8,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            pointerEvents: 'none',
            bgcolor: 'background.paper',
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
          }}
        >
          {PERSONAL_CASHFLOW_LEGEND.map((item) => (
            <Box key={item.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={
                  item.kind === 'line'
                    ? { width: 12, height: 2, bgcolor: item.color, flexShrink: 0 }
                    : { width: 10, height: 10, bgcolor: item.color, borderRadius: 0.25, flexShrink: 0 }
                }
              />
              <Typography variant="caption" color="text.secondary">
                {item.name}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box
          ref={chartRef}
          data-testid="personal-cashflow-chart-scroll"
          sx={{ width: '100%', height: 260 }}
        >
          {chartWidth > 1 ? (
            <ComposedChart
              width={chartWidth}
              height={260}
              data={chartRows}
              margin={{ top: 28, right: 8, left: 0, bottom: 0 }}
              barCategoryGap={2}
              barGap={1}
            >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10 }}
              interval={xInterval}
              angle={-90}
              textAnchor="end"
              height={64}
              tickMargin={6}
            />
            <YAxis
              tickFormatter={(value) =>
                new Intl.NumberFormat(undefined, {
                  style: 'currency',
                  currency: 'EUR',
                  maximumFractionDigits: 0,
                }).format(Number(value || 0))
              }
              tick={{ fontSize: 14 }}
              width={88}
            />
            <Tooltip content={<PersonalCashflowTooltip />} />
            <ReferenceLine y={0} stroke="#94a3b8" />
            <Line
              dataKey="net"
              name="Net"
              type="monotone"
              stroke={PERSONAL_CASHFLOW_NET_STROKE}
              strokeWidth={2}
              dot={chartMonths.length > 48 ? false : { r: 2 }}
              isAnimationActive={false}
            />
            <Bar
              dataKey="income"
              name="Income"
              fill={CASHFLOW_INCOME_FILL}
              barSize={barSize}
              isAnimationActive={false}
              cursor="pointer"
            >
              {chartRows.map((row) => (
                <Cell
                  key={`income-${row.month}`}
                  cursor="pointer"
                  onClick={() => {
                    const path = personalCashflowBarPath(household, row.month, 'income');
                    if (path) onView(path);
                  }}
                />
              ))}
            </Bar>
            <Bar
              dataKey="expensesChart"
              name="Expenses"
              fill={CASHFLOW_EXPENSES_FILL}
              barSize={barSize}
              isAnimationActive={false}
              cursor="pointer"
            >
              {chartRows.map((row) => (
                <Cell
                  key={`expenses-${row.month}`}
                  cursor="pointer"
                  onClick={() => {
                    const path = personalCashflowBarPath(household, row.month, 'expensesChart');
                    if (path) onView(path);
                  }}
                />
              ))}
            </Bar>
          </ComposedChart>
          ) : null}
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        All months (newest first)
      </Typography>
      <TableContainer
        data-testid="personal-cashflow-month-table"
        sx={{
          mb: 2,
          maxHeight: 320,
          overflowY: 'auto',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Month</TableCell>
              <TableCell align="right">Income</TableCell>
              <TableCell align="right">Expenses</TableCell>
              <TableCell align="right">Net</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {personalSpendMonthsNewestFirst(months).map((row) => (
              <TableRow key={row.month}>
                <TableCell>{row.label}</TableCell>
                <PersonalAmountView
                  formatted={formatCurrency(row.income)}
                  to={personalMonthFigurePath(household, row.month, 'income')}
                  ariaLabel={`View ${row.label} income`}
                  onView={onView}
                />
                <PersonalAmountView
                  formatted={formatCurrency(row.expenses)}
                  to={personalMonthFigurePath(household, row.month, 'expenses')}
                  ariaLabel={`View ${row.label} expenses`}
                  onView={onView}
                />
                <PersonalAmountView
                  formatted={formatCurrency(row.net)}
                  to={personalMonthFigurePath(household, row.month, 'net')}
                  ariaLabel={`View ${row.label} net`}
                  onView={onView}
                  sx={{ color: row.net < 0 ? 'error.main' : 'success.dark', fontWeight: 600 }}
                />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

/** Full dashboard UI is only for Omar (omarosullivan@gmail.com). Sean sees Rev Biz balances. OmHe family members see personal cashflow. Ask before adding widgets for other users. */
export const Dashboard: React.FC = () => {
  const DEFAULT_CRON_RUNS_COUNT = 1;
  const EXPANDED_CRON_RUNS_COUNT = 10;
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const viewerRole = user?.role;
  const userEmail = (user?.email || '').trim().toLowerCase();
  const isOmarDashboard = userEmail === 'omarosullivan@gmail.com';
  const isSeanDashboard =
    userEmail === 'sean.osullivan@gmail.com' || userEmail === 'seanosullivan@gmail.com';
  const personalHouseholds = (user?.personal_households || []).filter(
    (household): household is 'seco' | 'omhe' => household === 'seco' || household === 'omhe'
  );
  const isPersonalAccountsDashboard = !isOmarDashboard && !isSeanDashboard && personalHouseholds.length > 0;
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (loading) return;
    const id = window.location.hash.replace(/^#/, '');
    if (id !== 'bank-account-balances' && id !== 'monthly-performance') return;
    document.getElementById(id)?.scrollIntoView({ block: 'start' });
  }, [loading]);
  const [syncingNow, setSyncingNow] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  // Names of the dashboard sections whose data failed to load, surfaced in a toast.
  const [unavailableData, setUnavailableData] = useState<string[]>([]);
  const [unavailableToastOpen, setUnavailableToastOpen] = useState(false);
  // Live GBP→EUR rate (ECB via Frankfurter) used to show the EUR equivalent of the
  // UK Revolut balance, which is held in GBP. Falls back to the backend default.
  const GBP_EUR_FALLBACK_RATE = 1.16;
  const [gbpEurRate, setGbpEurRate] = useState<number>(GBP_EUR_FALLBACK_RATE);
  const [gbpEurRateDate, setGbpEurRateDate] = useState<string | null>(null);

  useEffect(() => {
    if (!isOmarDashboard && !isSeanDashboard) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/hosting-variables/exchange-rate/gbp-eur');
        const rate = Number(res?.data?.rate);
        if (!cancelled && Number.isFinite(rate) && rate > 0) {
          setGbpEurRate(rate);
          setGbpEurRateDate(res?.data?.date ? String(res.data.date) : null);
        }
      } catch {
        // Keep the fallback rate; EUR equivalent is still shown (approximate).
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOmarDashboard, isSeanDashboard]);
  const [expandedRunId, setExpandedRunId] = useState<number | false>(false);
  const [showMoreCronRuns, setShowMoreCronRuns] = useState(false);
  const [cronConfig, setCronConfig] = useState<CronConfig | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loans, setLoans] = useState<Loan[]>([]);
  const [revolutTransactions, setRevolutTransactions] = useState<RevolutTransaction[]>([]);
  const [loanBalanceHistory, setLoanBalanceHistory] = useState<LoanBalanceEntry[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailModalTitle, setDetailModalTitle] = useState('');
  const [detailModalItem, setDetailModalItem] = useState<Record<string, any> | null>(null);
  const [detailModalResolved, setDetailModalResolved] = useState<Record<string, any> | null>(null);
  const [detailModalLoading, setDetailModalLoading] = useState(false);
  const [manualReviewSummary, setManualReviewSummary] = useState<ManualReviewSummary | null>(null);
  const [manualBalances, setManualBalances] = useState<ManualBalanceEntry[]>([]);
  const [supplierVatPolicyRows, setSupplierVatPolicyRows] = useState<SupplierVatPolicyRow[]>([]);
  const [upcomingBookings, setUpcomingBookings] = useState<UpcomingBookingRow[]>([]);
  const [outlookBookings, setOutlookBookings] = useState<UpcomingBookingRow[]>([]);
  const [monthlyStats, setMonthlyStats] = useState<MonthlyBookingStats | null>(null);
  const [statsChartPropId, setStatsChartPropId] = useState<number | null>(null);
  const [personalSummaries, setPersonalSummaries] = useState<PersonalDashboardHouseholdSummary[]>([]);

  const personalHouseholdKey = (user?.personal_households || []).join(',');

  const fetchDashboardData = useCallback(async (showLoading = true, keepMessages = false) => {
    try {
      if (showLoading) setLoading(true);
      if (!keepMessages) {
        setError('');
        setInfoMessage('');
      }

      const email = (user?.email || '').trim().toLowerCase();
      const isSeanUser = email === 'sean.osullivan@gmail.com' || email === 'seanosullivan@gmail.com';
      const isOmarUser = email === 'omarosullivan@gmail.com';

      const clearDashboardState = () => {
        setCronConfig(null);
        setAccounts([]);
        setLoans([]);
        setRevolutTransactions([]);
        setLoanBalanceHistory([]);
        setProperties([]);
        setManualReviewSummary(null);
        setManualBalances([]);
        setSupplierVatPolicyRows([]);
        setUpcomingBookings([]);
        setOutlookBookings([]);
        setMonthlyStats(null);
        setPersonalSummaries([]);
      };

      if (!email) {
        if (showLoading) setLoading(false);
        return;
      }

      if (!isOmarUser && !isSeanUser) {
        const households = (user?.personal_households || []).filter(
          (household) => household === 'seco' || household === 'omhe'
        );
        if (households.length === 0) {
          clearDashboardState();
          if (showLoading) setLoading(false);
          return;
        }
        const summaryRes = await Promise.allSettled([api.get('/api/personal-finances/summary')]);
        clearDashboardState();
        if (summaryRes[0].status === 'fulfilled') {
          setPersonalSummaries(summaryRes[0].value.data?.households || []);
        } else {
          setPersonalSummaries([]);
          setError('Some data failed to load. Please try again.');
        }
        if (showLoading) setLoading(false);
        return;
      }

      if (isSeanUser) {
        // Sean's dashboard only needs the latest balance per Rev Biz account (Ire/UK) plus the most
        // recent pull time. Fetch a small, newest-first page per source instead of downloading 13
        // months of every transaction: that heavy all=true query timed out / 500'd on the
        // free-tier instance and surfaced as "Some data failed to load. Please try again."
        const SEAN_TX_PAGE_SIZE = 50;
        const [accountsRes, ireTxRes, ukTxRes] = await Promise.allSettled([
          api.get('/api/accounts/'),
          api.get('/api/revolut/transactions/complete', {
            params: { account: 'ireland', page: 1, per_page: SEAN_TX_PAGE_SIZE },
          }),
          api.get('/api/revolut/transactions/complete', {
            params: { account: 'uk', page: 1, per_page: SEAN_TX_PAGE_SIZE },
          }),
        ]);
        clearDashboardState();
        if (accountsRes.status === 'fulfilled') {
          const raw = accountsRes.value.data?.accounts || [];
          setAccounts(filterAccountsForSeanDashboard(raw));
        } else {
          setAccounts([]);
        }
        const combinedTxs: RevolutTransaction[] = [];
        for (const txRes of [ireTxRes, ukTxRes]) {
          if (txRes.status === 'fulfilled') {
            const rows = txRes.value.data?.transactions;
            if (Array.isArray(rows)) combinedTxs.push(...rows);
          }
        }
        setRevolutTransactions(filterRevolutTransactionsForSeanDashboard(combinedTxs));
        // Only surface the error when core data is genuinely missing: the account list failed, or
        // both balance sources failed (leaving nothing fresh to show). A single failed source still
        // leaves usable balances on screen, so it should not block the whole dashboard.
        if (
          accountsRes.status === 'rejected' ||
          (ireTxRes.status === 'rejected' && ukTxRes.status === 'rejected')
        ) {
          setError('Some data failed to load. Please try again.');
        }
        if (showLoading) setLoading(false);
        return;
      }

      // Initial load: small newest-first pages for IRE/UK balances (same idea as Sean).
      // The old all=true 13‑month dump is ~5MB / 2+ minutes over a remote Postgres tunnel and
      // kept the dashboard spinner stuck. Chart history is filled in the background below.
      const TX_PAGE_SIZE = 50;
      const [cronRes, accountsRes, loansRes, ireTxRes, ukTxRes, loanBalancesRes, propertiesRes, manualReviewRes, manualBalancesRes, supplierVatPoliciesRes, upcomingBookingsRes, outlookBookingsRes, monthlyStatsRes] = await Promise.allSettled([
        api.get('/api/cron/status'),
        api.get('/api/accounts/'),
        api.get('/api/loans/'),
        api.get('/api/revolut/transactions/complete', {
          params: { account: 'ireland', page: 1, per_page: TX_PAGE_SIZE },
        }),
        api.get('/api/revolut/transactions/complete', {
          params: { account: 'uk', page: 1, per_page: TX_PAGE_SIZE },
        }),
        api.get('/api/balances/loans'),
        api.get('/api/properties/'),
        api.get('/api/transaction-classification-rules/manual-review-count'),
        api.get('/api/manual-balances/'),
        api.get('/api/suppliers/vat-invoice-policies'),
        api.get('/api/calendar/unified/upcoming', { params: { scope: 'week' }, timeout: 60000 }),
        // Stays timeline: ~26 weeks of history + ~30 weeks ahead so the user can scroll
        // back/forward through past and future stays (backend caps both at 365 days).
        api.get('/api/calendar/unified/upcoming', { params: { scope: 'days', days: 224, days_back: 196 }, timeout: 60000 }),
        // Monthly performance: current month vs same month in previous years.
        api.get('/api/calendar/booking-monthly-stats', { timeout: 60000 }),
      ]);

      let endpointErrors = 0;
      // Friendly names of the sections whose data failed to load (for the error + toast).
      const failedData: string[] = [];

      if (cronRes.status === 'fulfilled') {
        const cfg = cronRes.value.data as CronConfig;
        setCronConfig(cfg);
        setExpandedRunId(false);
      } else {
        endpointErrors += 1;
        failedData.push('Cron status');
      }

      if (accountsRes.status === 'fulfilled') {
        setAccounts(accountsRes.value.data?.accounts || []);
      } else {
        endpointErrors += 1;
        failedData.push('Bank accounts');
      }

      if (loansRes.status === 'fulfilled') {
        setLoans(loansRes.value.data?.loans || []);
      } else {
        endpointErrors += 1;
        failedData.push('Loans');
      }

      const lightTxs: RevolutTransaction[] = [];
      for (const [txRes, txLabel] of [
        [ireTxRes, 'Revolut transactions (Ireland)'] as const,
        [ukTxRes, 'Revolut transactions (UK)'] as const,
      ]) {
        if (txRes.status === 'fulfilled') {
          const rows = txRes.value.data?.transactions;
          if (Array.isArray(rows)) lightTxs.push(...rows);
        } else {
          endpointErrors += 1;
          failedData.push(txLabel);
        }
      }
      setRevolutTransactions(lightTxs);

      // Background: full 13‑month series for the net-worth chart (never blocks the spinner).
      const now = new Date();
      const fromDate = new Date(now.getFullYear(), now.getMonth() - 13, 1).toISOString();
      void api
        .get('/api/revolut/transactions/complete', {
          params: { account: 'all', all: true, from_date: fromDate },
          timeout: 180000,
        })
        .then((res) => {
          const rows = res.data?.transactions;
          if (Array.isArray(rows) && rows.length > 0) {
            setRevolutTransactions(rows);
          }
        })
        .catch(() => {
          /* keep lightTxs — balances still show */
        });

      if (loanBalancesRes.status === 'fulfilled') {
        setLoanBalanceHistory(loanBalancesRes.value.data?.balances || []);
      } else {
        endpointErrors += 1;
        failedData.push('Loan balances');
      }

      if (propertiesRes.status === 'fulfilled') {
        setProperties(propertiesRes.value.data?.properties || []);
      } else {
        endpointErrors += 1;
        failedData.push('Properties');
      }

      if (manualReviewRes.status === 'fulfilled') {
        setManualReviewSummary(manualReviewRes.value.data || null);
      } else {
        setManualReviewSummary(null);
        failedData.push('Manual review count');
      }

      if (manualBalancesRes.status === 'fulfilled') {
        setManualBalances(manualBalancesRes.value.data?.balances || []);
      } else {
        setManualBalances([]);
        failedData.push('Manual balances');
      }

      if (supplierVatPoliciesRes.status === 'fulfilled') {
        setSupplierVatPolicyRows(Array.isArray(supplierVatPoliciesRes.value.data?.rows) ? supplierVatPoliciesRes.value.data.rows : []);
      } else {
        setSupplierVatPolicyRows([]);
        failedData.push('Supplier VAT policies');
      }

      if (upcomingBookingsRes.status === 'fulfilled') {
        setUpcomingBookings(upcomingBookingsRes.value.data?.bookings || []);
      } else {
        setUpcomingBookings([]);
        failedData.push('Upcoming bookings');
      }

      if (outlookBookingsRes.status === 'fulfilled') {
        setOutlookBookings(outlookBookingsRes.value.data?.bookings || []);
      } else {
        setOutlookBookings([]);
        failedData.push('Stays timeline');
      }

      if (monthlyStatsRes.status === 'fulfilled') {
        setMonthlyStats(monthlyStatsRes.value.data || null);
      } else {
        setMonthlyStats(null);
        failedData.push('Monthly performance stats');
      }

      if (endpointErrors >= 3) {
        const list = failedData.length ? failedData.join(', ') : 'several sections';
        setError(`Some dashboard data is currently unavailable: ${list}.`);
        setUnavailableData(failedData);
        setUnavailableToastOpen(true);
      } else {
        setUnavailableData([]);
        setUnavailableToastOpen(false);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load dashboard data');
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [user?.email, personalHouseholdKey]);

  useEffect(() => {
    void fetchDashboardData();
  }, [fetchDashboardData]);

  const handleSyncNow = async () => {
    try {
      setSyncingNow(true);
      setError('');
      setInfoMessage('');
      const response = await api.post('/api/cron/sync');
      const runAt = response?.data?.timestamp || new Date().toISOString();
      const status = response?.data?.status || 'success';
      const results = response?.data?.results || null;
      const emailReport = response?.data?.email_report || null;
      const resultsWithEmail = results && typeof results === 'object'
        ? { ...results, email_report: emailReport || (results as Record<string, any>).email_report }
        : (emailReport ? { email_report: emailReport } : results);

      if (emailReport?.sent) {
        const recipient = emailReport?.to ? String(emailReport.to) : '';
        setInfoMessage(
          recipient
            ? `Manual Run Completed Successfully and cron email sent to ${recipient}`
            : 'Manual Run Completed Successfully and cron email sent'
        );
      } else if (emailReport) {
        const reason = emailReport?.error || emailReport?.reason || 'unknown reason';
        setError(`Manual Run Completed Successfully, but cron email was not sent: ${reason}`);
      }

      // Show the just-completed manual sync immediately in the panel.
      const tempRunId = Date.now();
      setCronConfig((prev) => {
        if (!prev) return prev;
        const nextRuns = [
          {
            id: tempRunId,
            run_at: runAt,
            status,
            message:
              status === 'success'
                ? 'Manual Run Completed Successfully'
                : (status === 'partial' ? 'Manual Run Completed With Issues' : 'Manual Run Finished'),
            details: resultsWithEmail
          },
          ...(prev.recent_runs || [])
        ].slice(0, CRON_JOBS_PANEL_MAX);

        return {
          ...prev,
          last_run_at: runAt,
          last_run_status: status,
          last_run_message: status === 'success' ? 'Manual Run Completed Successfully' : prev.last_run_message,
          last_run_details: resultsWithEmail,
          recent_runs: nextRuns
        };
      });
      setExpandedRunId(false);
      await fetchDashboardData(false, true);
      window.setTimeout(() => {
        void fetchDashboardData(false, true);
      }, 2500);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to run sync now');
    } finally {
      setSyncingNow(false);
    }
  };

  const formatDateTime = (iso: string | null | undefined) => {
    if (!iso) return 'Never';
    const hasTimezone = /([zZ]|[+-]\d{2}:\d{2})$/.test(iso);
    const normalizedIso = hasTimezone ? iso : `${iso}Z`;
    const date = new Date(normalizedIso);
    if (Number.isNaN(date.getTime())) return iso;
    return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  };

  const formatBookingDate = (iso: string | null | undefined) => {
    if (!iso) return '—';
    const date = new Date(`${iso.slice(0, 10)}T12:00:00`);
    if (Number.isNaN(date.getTime())) return iso;
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const weekStayGroups = useMemo(() => {
    const { today, weekStart, weekEnd } = getCalendarWeekBounds();
    const currentStays = upcomingBookings
      .filter((booking) => isCurrentStay(booking, today))
      .sort((a, b) => a.check_in_date.localeCompare(b.check_in_date));
    const upcomingStays = upcomingBookings
      .filter((booking) => isUpcomingStayThisWeek(booking, today, weekEnd))
      .sort((a, b) => a.check_in_date.localeCompare(b.check_in_date));
    return { today, weekStart, weekEnd, currentStays, upcomingStays };
  }, [upcomingBookings]);

  // Timeline: one horizontal lane per property. Stays for a property never overlap,
  // so they are all laid out inline on the same lane at their date positions.
  // The window is wide (weeks back + weeks ahead) and rendered at a fixed pixel width
  // per day so the panel scrolls horizontally to reveal past/future stays.
  const stayTimeline = useMemo(() => {
    const MS_PER_DAY = 24 * 60 * 60 * 1000;
    const WEEKS_BACK = 26; // history the user can scroll back to
    const WEEKS_AHEAD = 30; // future the user can scroll forward to
    const PX_PER_DAY = 22; // fixed day width -> total width overflows -> scrolls
    const { weekStart } = getCalendarWeekBounds();
    const rangeStart = new Date(weekStart);
    rangeStart.setDate(weekStart.getDate() - WEEKS_BACK * 7); // Monday, WEEKS_BACK weeks ago
    rangeStart.setHours(0, 0, 0, 0);
    const TOTAL_WEEKS = WEEKS_BACK + WEEKS_AHEAD;
    const TOTAL_DAYS = TOTAL_WEEKS * 7;
    const rangeEnd = new Date(rangeStart);
    rangeEnd.setDate(rangeStart.getDate() + TOTAL_DAYS);
    rangeEnd.setHours(0, 0, 0, 0);

    const dayOffset = (d: Date) => (d.getTime() - rangeStart.getTime()) / MS_PER_DAY;
    const px = (day: number) => day * PX_PER_DAY;
    const totalWidthPx = TOTAL_DAYS * PX_PER_DAY;

    const inWindow = outlookBookings.filter((b) => {
      const ci = parseBookingIsoDate(b.check_in_date);
      const co = parseBookingIsoDate(b.check_out_date);
      return co > rangeStart && ci < rangeEnd;
    });

    // Colour encodes the payout amount (blue = small, red = big); the lane already
    // identifies the property, so colour is free to carry a second dimension.
    const payoutOf = (b: UpcomingBookingRow): number | null => {
      const raw = b.total_payout_amount ?? b.host_payout_amount;
      const val = raw == null || raw === '' ? NaN : Number(raw);
      return Number.isFinite(val) && val > 0 ? val : null;
    };
    const anyPayout = inWindow.some((b) => payoutOf(b) != null);
    const NEUTRAL_COLOR = theme.palette.mode === 'dark' ? '#5a5a5a' : '#bdbdbd';
    // Straight two-colour blend from blue to red, so it reads as a single scale
    // rather than a rainbow. The scale is RELATIVE to each property: a payout is
    // coloured against that property's own min/max, so the same € figure can be
    // blue for a high-value property and red for a lower-value one.
    const PAYOUT_LOW_RGB = [21, 101, 192]; // blue  (#1565c0)
    const PAYOUT_HIGH_RGB = [198, 40, 40]; // red   (#c62828)
    const colorForPayout = (payout: number | null, lo: number, hi: number): string => {
      if (payout == null) return NEUTRAL_COLOR;
      const span = hi - lo;
      const t = span > 0 ? Math.min(1, Math.max(0, (payout - lo) / span)) : 0.5;
      const mix = (i: number) => Math.round(PAYOUT_LOW_RGB[i] + (PAYOUT_HIGH_RGB[i] - PAYOUT_LOW_RGB[i]) * t);
      return `rgb(${mix(0)}, ${mix(1)}, ${mix(2)})`;
    };

    // Group bookings into one lane per property (sorted by property name).
    const propertyNames = Array.from(
      new Set(inWindow.map((b) => b.property_nickname || 'Unknown')),
    ).sort((a, b) => a.localeCompare(b));

    const lanes = propertyNames.map((prop) => {
      const rows = inWindow
        .filter((b) => (b.property_nickname || 'Unknown') === prop)
        .sort((a, b) => a.check_in_date.localeCompare(b.check_in_date));
      // Per-property payout range so colouring is relative to this property only.
      const lanePayouts = rows.map(payoutOf).filter((v): v is number => v != null);
      const laneMin = lanePayouts.length ? Math.min(...lanePayouts) : 0;
      const laneMax = lanePayouts.length ? Math.max(...lanePayouts) : 0;
      const stays = rows
        .map((b) => {
          const ci = parseBookingIsoDate(b.check_in_date);
          const co = parseBookingIsoDate(b.check_out_date);
          const startDay = Math.max(0, dayOffset(ci));
          const endDay = Math.min(TOTAL_DAYS, dayOffset(co));
          const widthDays = Math.max(endDay - startDay, 0.5);
          const nights = Math.max(1, Math.round((co.getTime() - ci.getTime()) / MS_PER_DAY));
          const adults = Number(b.adults_count || 0);
          const children = Number(b.children_count || 0);
          const payout = payoutOf(b);
          return {
            id: b.id,
            guest: b.guest_name || 'Guest',
            checkIn: b.check_in_date,
            checkOut: b.check_out_date,
            nights,
            source: b.source || null,
            status: b.status || null,
            guests: adults + children,
            adults,
            children,
            payout,
            color: colorForPayout(payout, laneMin, laneMax),
            currency: b.currency || null,
            reservationUrl: (b.reservation_url || '').trim() || null,
            externalId: (b.external_booking_id || '').trim() || null,
            mergedCount: Array.isArray(b.merged_booking_ids) ? b.merged_booking_ids.length : 0,
            flightIn: b.flight_in_display || null,
            flightOut: b.flight_out_display || null,
            leftPx: px(startDay),
            // Trim 1px so back-to-back stays (checkout day == next check-in day) have a
            // visible 1px gap instead of touching.
            widthPx: Math.max(px(widthDays) - 1, 3),
            clippedStart: dayOffset(ci) < 0,
            clippedEnd: dayOffset(co) > TOTAL_DAYS,
          };
        });
      return { property: prop, stays };
    });

    // One cell per day, labelled with its weekday letter. rangeStart is a Monday,
    // so day index % 7 maps to M T W T F S S.
    const DAY_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
    const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dayCells = Array.from({ length: TOTAL_DAYS }, (_, d) => {
      const cellDate = new Date(rangeStart);
      cellDate.setDate(rangeStart.getDate() + d);
      return {
        letter: DAY_LETTERS[d % 7],
        dom: cellDate.getDate(), // DD part of the date
        isWeekend: d % 7 >= 5,
      };
    });
    // Month header bands: contiguous runs of days in the same calendar month.
    const monthBands: Array<{ label: string; leftPx: number; widthPx: number }> = [];
    for (let d = 0; d < TOTAL_DAYS; d += 1) {
      const cellDate = new Date(rangeStart);
      cellDate.setDate(rangeStart.getDate() + d);
      const m = cellDate.getMonth();
      const y = cellDate.getFullYear();
      // Show the year alongside January to mark year boundaries.
      const label = m === 0 ? `${MONTH_NAMES[m]} ${String(y).slice(-2)}` : MONTH_NAMES[m];
      const last = monthBands[monthBands.length - 1];
      if (last && last.label === label) {
        last.widthPx += PX_PER_DAY;
      } else {
        monthBands.push({ label, leftPx: px(d), widthPx: PX_PER_DAY });
      }
    }
    // Weekend bands (Sat–Sun) for each week, as left/width pixels.
    const weekendBands = Array.from({ length: TOTAL_WEEKS }, (_, i) => ({
      leftPx: px(i * 7 + 5),
      widthPx: px(2),
    }));
    // Vertical separator at the start of each week (Monday).
    const weekLines = Array.from({ length: TOTAL_WEEKS + 1 }, (_, i) => px(i * 7));

    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const todayOff = dayOffset(now);
    const todayPx = todayOff >= 0 && todayOff <= TOTAL_DAYS ? px(todayOff) : null;

    const totalStays = lanes.reduce((sum, lane) => sum + lane.stays.length, 0);

    return {
      lanes,
      TOTAL_DAYS,
      PX_PER_DAY,
      totalWidthPx,
      dayCells,
      monthBands,
      weekendBands,
      weekLines,
      todayPx,
      rangeStart,
      rangeEnd,
      count: totalStays,
      laneCount: lanes.length,
      hasPayoutScale: anyPayout,
    };
  }, [outlookBookings, theme]);

  // Horizontal scroll container for the stays timeline; auto-centre on "today" when
  // the data first loads so the user starts at the current week and can scroll
  // left (history) or right (future).
  const timelineScrollRef = useRef<HTMLDivElement | null>(null);
  const timelineAutoScrolledRef = useRef(false);
  useEffect(() => {
    timelineAutoScrolledRef.current = false;
  }, [outlookBookings]);
  useEffect(() => {
    const el = timelineScrollRef.current;
    if (!el || stayTimeline.count === 0 || timelineAutoScrolledRef.current) return;
    if (stayTimeline.todayPx == null) return;
    // Place today roughly one-third in from the left edge of the visible track.
    const target = Math.max(0, stayTimeline.todayPx - el.clientWidth / 3);
    el.scrollLeft = target;
    timelineAutoScrolledRef.current = true;
  }, [stayTimeline]);

  const openBookingOnBookingsPage = (booking: UpcomingBookingRow) => {
    navigate(`/bookings/${booking.id}`);
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(value);

  const formatMoney = (value: number | null | undefined, currency?: string | null) => {
    if (value == null || !Number.isFinite(Number(value))) return '—';
    const cur = currency && currency.length === 3 ? currency : 'EUR';
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur, maximumFractionDigits: 2 }).format(
      Number(value)
    );
  };

  const formatBytes = (value: number | null | undefined) => {
    if (value == null || !Number.isFinite(Number(value))) return 'n/a';
    let n = Math.abs(Number(value));
    const sign = Number(value) < 0 ? '-' : '';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let idx = 0;
    while (n >= 1024 && idx < units.length - 1) {
      n /= 1024;
      idx += 1;
    }
    return `${sign}${n.toFixed(2)} ${units[idx]}`;
  };

  const formatMs = (value: number | null | undefined) => {
    if (value == null || !Number.isFinite(Number(value))) return 'n/a';
    const ms = Number(value);
    if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
    return `${ms.toFixed(0)}ms`;
  };

  const getCount = (value: number | string | { count?: number } | undefined | null) => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    if (value && typeof value === 'object' && typeof value.count === 'number') return value.count;
    return 0;
  };

  const getItems = (value: number | string | { items?: Array<Record<string, any>> } | undefined | null) => {
    if (value && typeof value === 'object' && Array.isArray(value.items)) return value.items;
    return [];
  };

  /** Cron booking samples: show legacy rows without `outcome`; hide `unchanged` when tagged. */
  const getNewOrChangedBookingItems = (items: Array<Record<string, any>> | undefined) => {
    const list = items || [];
    return list.filter((item) => {
      const o = item?.outcome;
      if (o == null || o === '') return true;
      return o === 'created' || o === 'updated';
    });
  };

  const getSyncError = (
    value:
      | number
      | string
      | { count?: number; items?: Array<Record<string, any>> }
      | { error?: string | null; attempts?: number }
      | CronAccountSyncDetails
      | undefined
      | null
  ) => {
    if (!value || typeof value !== 'object') return null;
    const obj = value as { error?: unknown; attempts?: unknown };
    const error = typeof obj.error === 'string' ? obj.error : null;
    if (!error) return null;
    const attempts = typeof obj.attempts === 'number' ? obj.attempts : 1;
    return `${error} (attempts: ${attempts})`;
  };

  const formatBackupSummary = (backup?: CronBackupDetails | null) => {
    if (!backup || backup.success == null) return 'Backup: n/a';
    return backup.success ? 'Backup: success' : `Backup: ${backup.status || 'failed'}`;
  };

  const formatStorageProvider = (provider?: string | null) => {
    switch ((provider || '').trim()) {
      case 'dropbox':
        return 'Dropbox';
      case 'hetzner_s3':
        return 'S3';
      case 'local_fs':
        return 'local storage';
      default:
        return 'cloud storage';
    }
  };

  const truncateCronHint = (text: string, max = 110) => {
    const t = String(text || '').trim();
    if (!t) return '';
    return t.length > max ? `${t.slice(0, max - 1)}…` : t;
  };

  const getBackupSummaryLine = (backup?: CronBackupDetails | null) => {
    let text = formatBackupSummary(backup);
    const failed = backup != null && backup.success === false;
    const err = typeof backup?.error === 'string' ? backup.error.trim() : '';
    if (failed && err) text = `${text} (${truncateCronHint(err)})`;
    return { failed, text };
  };

  const getRevolutLine = (
    revolut:
      | {
          success?: boolean;
          error?: string;
          transactions?: Record<string, Parameters<typeof getCount>[0]>;
          expenses?: Record<string, Parameters<typeof getCount>[0]>;
        }
      | undefined
      | null,
    kind: 'transactions' | 'expenses'
  ) => {
    const bucket = revolut?.[kind];
    const ieRaw = bucket?.ireland;
    const ukRaw = bucket?.uk;
    const ie = getCount(ieRaw);
    const uk = getCount(ukRaw);
    const errIe = getSyncError(ieRaw);
    const errUk = getSyncError(ukRaw);
    const top =
      revolut && revolut.success === false && typeof revolut.error === 'string' && revolut.error.trim()
        ? revolut.error.trim()
        : '';
    const failed = Boolean(top || errIe || errUk);
    let suffix = '';
    if (top) suffix = ` — Revolut: failed (${truncateCronHint(top)})`;
    else if (errIe || errUk) {
      const bits: string[] = [];
      if (errIe) bits.push(`IE ${truncateCronHint(errIe)}`);
      if (errUk) bits.push(`UK ${truncateCronHint(errUk)}`);
      suffix = ` — Revolut: failed (${bits.join(' · ')})`;
    } else if (ie === 0 && uk === 0) suffix = ' — Revolut: OK (0 new this run)';
    else suffix = ' — Revolut: OK';
    const prefix =
      kind === 'transactions' ? `Revolut Tx: IE ${ie}, UK ${uk}` : `Expenses: IE ${ie}, UK ${uk}`;
    return { failed, text: `${prefix}${suffix}` };
  };

  const getUpdatedCount = (value: unknown): number => {
    if (!value || typeof value !== 'object') return 0;
    const v = value as { updated_count?: unknown };
    const n = Number(v.updated_count);
    return Number.isFinite(n) ? n : 0;
  };

  const getRevolutUpdatedLine = (
    revolut:
      | {
          transactions?: Record<string, unknown>;
          expenses?: Record<string, unknown>;
        }
      | undefined
      | null,
    kind: 'transactions' | 'expenses'
  ) => {
    const bucket = revolut?.[kind] || {};
    const ie = getUpdatedCount((bucket as Record<string, unknown>).ireland);
    const uk = getUpdatedCount((bucket as Record<string, unknown>).uk);
    const label = kind === 'transactions' ? 'TX Updated' : 'Expenses Updated';
    return { failed: false, text: `${label}: IE ${ie}, UK ${uk}` };
  };

  const getBookingsSummaryLine = (
    bookings:
      | {
          synced_count?: number;
          updated_count?: number;
          unchanged_count?: number;
          processed_count?: number;
          errors?: string[];
          success?: boolean;
          error?: string;
        }
      | undefined
      | null
  ) => {
    const synced = Number(bookings?.synced_count ?? 0);
    const updated = Number(bookings?.updated_count ?? 0);
    const unchanged = Number(bookings?.unchanged_count ?? 0);
    const processed = Number(bookings?.processed_count ?? 0);
    const errTop = typeof bookings?.error === 'string' ? bookings.error.trim() : '';
    const rawBookingErrors = bookings?.errors;
    const errs = Array.isArray(rawBookingErrors)
      ? (rawBookingErrors as unknown[]).map((e) => String(e).trim()).filter(Boolean)
      : [];
    const failed = bookings?.success === false || errs.length > 0 || Boolean(errTop);
    let suffix = '';
    if (errTop) suffix = ` — iCal: failed (${truncateCronHint(errTop)})`;
    else if (errs.length) suffix = ` — iCal: failed (${truncateCronHint(errs.join(' · '))})`;
    else suffix = ' — iCal: OK';
    return {
      failed,
      text: `Bookings: ${synced} new / ${updated} updated / ${unchanged} unchanged / ${processed} pulled${suffix}`,
    };
  };

  const getE2ePlaywrightLine = (details: unknown) => {
    const d = details as {
      run_kind?: string;
      passed?: number;
      failed?: number;
      skipped?: number;
      tests_total?: number;
      workflow_run_url?: string;
    } | null;
    if (!d || d.run_kind !== 'playwright_e2e') return { failed: false, text: '' };
    const p = Number(d.passed ?? 0);
    const f = Number(d.failed ?? 0);
    const s = Number(d.skipped ?? 0);
    const tot = Number(d.tests_total ?? 0);
    const failed = f > 0;
    const wf = d.workflow_run_url ? ` · ${d.workflow_run_url}` : '';
    const totPart = tot > 0 ? ` (${tot} tests)` : '';
    return {
      failed,
      text: `Playwright E2E${totPart}: ${p} passed, ${f} failed, ${s} skipped${wf}`,
    };
  };

  const getPerformanceSummaryLine = (performance?: CronPerformanceDetails | null) => {
    if (!performance) {
      return { failed: false, text: 'Performance: n/a' };
    }
    const timings = performance.timings_ms || {};
    const memory = performance.memory || {};
    const restart = performance.restart_signals || {};
    const peak = memory.peak_observed_bytes ?? memory.peak_cgroup_bytes ?? memory.peak_max_rss_bytes;
    const failed = restart.pid_changed_since_last_run === true;
    const restartHint = restart.pid_changed_since_last_run == null
      ? 'restart signal n/a'
      : restart.pid_changed_since_last_run
        ? 'pid changed since last run'
        : 'pid stable';
    return {
      failed,
      text:
        `Performance: total ${formatMs(timings.total)} | ` +
        `peak ${formatBytes(peak)} | ` +
        `RSS delta ${formatBytes(memory.rss_delta_bytes)} | ${restartHint}`,
    };
  };

  const getPerformanceDisplay = (details: any) => {
    const performance = (details?.performance || null) as CronPerformanceDetails | null;
    const timings = performance?.timings_ms || {};
    const legacyTimings = details?.stage_timings_ms || {};
    const memory = performance?.memory || {};
    const restart = performance?.restart_signals || {};

    const backupMs = Number.isFinite(Number(timings.backup))
      ? Number(timings.backup)
      : (Number.isFinite(Number(legacyTimings.backup)) ? Number(legacyTimings.backup) : null);
    const revolutMs = Number.isFinite(Number(timings.revolut))
      ? Number(timings.revolut)
      : (Number.isFinite(Number(legacyTimings.revolut)) ? Number(legacyTimings.revolut) : null);
    const bookingsMs = Number.isFinite(Number(timings.bookings))
      ? Number(timings.bookings)
      : (Number.isFinite(Number(legacyTimings.bookings)) ? Number(legacyTimings.bookings) : null);

    const stageMs = [backupMs, revolutMs, bookingsMs].filter((v): v is number => Number.isFinite(Number(v)));
    const totalMs = Number.isFinite(Number(timings.total))
      ? Number(timings.total)
      : stageMs.length > 0
        ? stageMs.reduce((acc, v) => acc + v, 0)
        : null;

    const txTotal =
      getCount(details?.revolut?.transactions?.ireland) + getCount(details?.revolut?.transactions?.uk);
    const expTotal =
      getCount(details?.revolut?.expenses?.ireland) + getCount(details?.revolut?.expenses?.uk);
    const bookingsProcessed = Number(details?.bookings?.processed_count ?? 0);
    const workloadText = `workload: ${txTotal} tx, ${expTotal} expenses, ${bookingsProcessed} bookings`;

    const peak = memory.peak_observed_bytes ?? memory.peak_cgroup_bytes ?? memory.peak_max_rss_bytes;
    const hasMemory =
      memory.start_rss_bytes != null ||
      memory.end_rss_bytes != null ||
      memory.rss_delta_bytes != null ||
      peak != null;
    const hasTiming = totalMs != null || stageMs.length > 0;
    const hasRestartSignal = restart.pid_changed_since_last_run != null;
    const restartHint = hasRestartSignal
      ? (restart.pid_changed_since_last_run ? 'PID changed since last run' : 'PID stable since last run')
      : `Not captured (${workloadText})`;

    const peakNum = Number(peak);
    const totalNum = Number(totalMs);
    const deltaNum = Number(memory.rss_delta_bytes);
    const hasPeak = Number.isFinite(peakNum);
    const hasTotal = Number.isFinite(totalNum);
    const hasDelta = Number.isFinite(deltaNum);
    const critical =
      (hasTotal && totalNum >= 180000) ||
      (hasPeak && peakNum >= 1200 * 1024 * 1024) ||
      (hasDelta && deltaNum >= 500 * 1024 * 1024);
    const warning =
      restart.pid_changed_since_last_run === true ||
      !hasTiming ||
      !hasMemory ||
      (hasTotal && totalNum >= 60000) ||
      (hasPeak && peakNum >= 700 * 1024 * 1024) ||
      (hasDelta && deltaNum >= 150 * 1024 * 1024);
    const healthState: CronHealthState = critical ? 'critical' : warning ? 'warning' : 'good';
    const healthLabel =
      healthState === 'good' ? 'All Good' : healthState === 'warning' ? 'Warning' : 'Critical Problem';

    const summaryBase = hasTiming || hasMemory
      ? getPerformanceSummaryLine(performance).text
      : `Performance: ${workloadText}; timings not captured`;
    const summary = `${summaryBase} | ${healthLabel}`;

    return {
      failed: healthState === 'critical',
      healthState,
      healthLabel,
      summary,
      totalDuration: totalMs != null ? formatMs(totalMs) : `Not captured (${workloadText})`,
      stageTimings:
        stageMs.length > 0
          ? `backup ${formatMs(backupMs)} | revolut ${formatMs(revolutMs)} | bookings ${formatMs(bookingsMs)}`
          : `Not captured (${workloadText})`,
      rssTriplet:
        hasMemory
          ? `${formatBytes(memory.start_rss_bytes)} / ${formatBytes(memory.end_rss_bytes)} / ${formatBytes(memory.rss_delta_bytes)}`
          : `Not captured (${workloadText})`,
      peakMemory: hasMemory ? formatBytes(peak) : `Not captured (${workloadText})`,
      restartHint,
    };
  };

  const getCronTriggerTypeLabel = (message: string | null | undefined): string => {
    const m = String(message || '');
    if (/playwright/i.test(m)) return 'Nightly E2E (CI)';
    if (/manual run/i.test(m)) return 'Manual';
    if (/cron job/i.test(m)) return 'Scheduled';
    return 'Unknown';
  };

  const getCronRunStatusLabel = (message: string | null | undefined, runStatus: string): string => {
    const m = String(message || '').trim();
    if (m) {
      const stripped = m.replace(/^Manual Run\s+/i, '').replace(/^Cron Job\s+/i, '').trim();
      if (stripped) return stripped;
    }
    if (runStatus === 'e2e_ok') return 'E2E passed';
    if (runStatus === 'e2e_bad') return 'E2E failed';
    if (runStatus === 'success') return 'Completed Successfully';
    if (runStatus === 'partial') return 'Completed With Issues';
    if (runStatus === 'error') return 'Failed';
    return runStatus || 'Unknown';
  };

  const getEmailConfirmationSummary = (emailReport?: CronEmailReport | null): string => {
    if (!emailReport) return 'Not available';
    if (emailReport.sent) {
      const to = emailReport.to ? String(emailReport.to) : '';
      return to ? `Success - sent to ${to}` : 'Success';
    }
    if (emailReport.skipped) return `Skipped - ${emailReport.reason || 'disabled'}`;
    return `Failed - ${emailReport.error || 'unknown error'}`;
  };

  const formatBackupSize = (sizeBytes: number | undefined) => {
    if (!Number.isFinite(Number(sizeBytes)) || Number(sizeBytes) <= 0) return null;
    return `${(Number(sizeBytes) / 1024 / 1024).toFixed(2)} MB`;
  };

  /** Dropbox folder where DB backups land (v3 app folder). Shown even when upload failed. */
  const DROPBOX_DB_BACKUPS_FOLDER_URL =
    'https://www.dropbox.com/home/Apps/family-fin-v3/Family%20Finance%20DB%20Backups';

  const openDetailModal = (title: string, item: Record<string, any>) => {
    setDetailModalTitle(title);
    setDetailModalItem(item);
    setDetailModalResolved(null);
    setDetailModalLoading(false);
    setDetailModalOpen(true);
  };

  const truncateText = (value: unknown, max = 80) => {
    const text = String(value ?? '').trim();
    if (!text) return '';
    return text.length > max ? `${text.slice(0, max - 1)}...` : text;
  };

  const humanizeFieldName = (key: string) =>
    key
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const normalizeRunStateLabel = (stateValue: unknown) => {
    const raw = String(stateValue ?? '').trim().toLowerCase();
    if (!raw) return '';
    if (raw === 'missing_info') return 'needs details';
    return raw.replace(/_/g, ' ');
  };

  const formatCronItemSummary = (
    item: Record<string, any>,
    kind: 'transaction' | 'expense' | 'booking'
  ) => {
    if (kind === 'transaction') {
      const primary =
        truncateText(item.description) ||
        truncateText(effectiveMerchantName(item) || item.merchant_name) ||
        truncateText(item.type) ||
        truncateText(item.revolut_id) ||
        'Transaction';
      const amountText =
        typeof item.amount_display === 'string'
          ? item.amount_display
          : formatMoney(
              Number.isFinite(Number(item.amount_signed)) ? Number(item.amount_signed) : Number(item.amount),
              item.currency
            );
      return `${primary} (${amountText})`;
    }
    if (kind === 'expense') {
      const primary =
        truncateText(item.merchant_name) ||
        truncateText(item.merchant) ||
        truncateText(item.payer) ||
        truncateText(item.description) ||
        truncateText(item.revolut_id) ||
        'Expense';
      const amountText = formatMoney(
        item.spent_amount?.value ?? item.amount?.value ?? item.spent_amount ?? item.amount,
        item.spent_amount?.currency ?? item.amount?.currency ?? item.currency
      );
      return `${primary} (${amountText})`;
    }
    const orderedBookingKeys = [
      'platform',
      'property',
      'guest_name',
      'check_in_date',
      'check_out_date',
      'nights',
      'adults_count',
      'children_count',
      'status',
      'source',
      'currency',
      'total_payout',
      'summary',
    ];
    const entries = Object.entries(item || {}).filter(([key, value]) => {
      const k = key.toLowerCase();
      if (k === 'id' || k.endsWith('_id')) return false;
      if (value == null) return false;
      if (typeof value === 'string' && value.trim() === '') return false;
      return true;
    });
    const valueByKey = new Map(entries);
    const orderedKeys = [
      ...orderedBookingKeys.filter((k) => valueByKey.has(k)),
      ...entries.map(([k]) => k).filter((k) => !orderedBookingKeys.includes(k)),
    ];
    const bookingParts = orderedKeys.map((key) => `${humanizeFieldName(key)}: ${truncateText(valueByKey.get(key), 90)}`);
    return bookingParts.join(' | ') || 'Booking';
  };

  const formatCronItemMeta = (
    item: Record<string, any>,
    kind: 'transaction' | 'expense' | 'booking'
  ): string[] => {
    const meta: string[] = [];
    const txType = truncateText(item.transaction_type || item.type);
    const state = truncateText(normalizeRunStateLabel(item.state || item.status));
    const dateTimeRaw =
      item.completed_at ||
      item.created_at_revolut ||
      item.expense_date ||
      item.created_at ||
      item.updated_at;
    const dateTimeText = dateTimeRaw ? formatDateTime(String(dateTimeRaw)) : '';
    const balanceAfterRaw = item.balance_after ?? item.balance ?? item.running_balance;
    const balanceCurrency =
      item.balance_currency ||
      item.currency ||
      item.spent_amount?.currency ||
      item.amount?.currency;

    if (balanceAfterRaw != null && Number.isFinite(Number(balanceAfterRaw))) {
      meta.push(`Balance after: ${formatMoney(Number(balanceAfterRaw), balanceCurrency)}`);
    }
    if (dateTimeText && dateTimeText !== 'Never') {
      meta.push(`Date/Time: ${dateTimeText}`);
    }
    if (state) {
      meta.push(`State: ${state}`);
    }
    if (txType) {
      meta.push(`Type: ${txType}`);
    }

    if (kind === 'booking' && meta.length === 0) {
      const checkIn = truncateText(item.check_in || item.checkin);
      const checkOut = truncateText(item.check_out || item.checkout);
      if (checkIn || checkOut) {
        meta.push(`Stay: ${checkIn || '—'} to ${checkOut || '—'}`);
      }
    }

    return meta;
  };

  const cronDetailSectionHeaderSx = {
    fontWeight: 700,
    fontSize: '0.9375rem',
    lineHeight: 1.35,
    mb: 0.75,
    color: 'text.primary',
  } as const;

  const renderRunSection = (
    sectionHeader: string,
    items: Array<Record<string, any>>,
    kind: 'transaction' | 'expense' | 'booking',
    hrefFactory: (item: Record<string, any>) => string,
    linkLabel: string,
    modalTitle: string
  ) => (
    <Box sx={{ mb: 1.5 }}>
      <Typography component="h4" variant="subtitle2" sx={cronDetailSectionHeaderSx}>
        {sectionHeader}
      </Typography>
      {items.length > 0 ? (
        <Box component="ol" sx={{ mt: 0.3, mb: 0, pl: 3.2 }}>
          {items.map((item, idx) => {
            const itemMeta = formatCronItemMeta(item, kind);
            const itemHref = hrefFactory(item);
            return (
              <Box
                component="li"
                key={String(item?.revolut_id || item?.booking_id || item?.id || idx)}
                sx={{ color: 'text.secondary', mb: 0.35, pl: 0.2 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {formatCronItemSummary(item, kind)}
                  {itemMeta.length > 0 ? ` - ${itemMeta.join(' | ')}` : ''}
                  {' - '}
                  <Link href={itemHref} underline="hover">
                    {linkLabel}
                  </Link>{' '}
                  <Button
                    size="small"
                    variant="text"
                    sx={{ minWidth: 0, p: 0, fontSize: 'inherit', lineHeight: 'inherit', textTransform: 'none' }}
                    onClick={() => openDetailModal(modalTitle, item)}
                  >
                    View here
                  </Button>
                </Typography>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Typography variant="caption" display="block" color="text.secondary">
          no new items.
        </Typography>
      )}
    </Box>
  );

  const getRunItemHref = (
    kind: 'transaction' | 'expense' | 'booking',
    item: Record<string, any>
  ) => {
    if (kind === 'transaction') {
      const params = new URLSearchParams({ tab: 'transactions' });
      const revolutId = String(item?.revolut_id || '').trim();
      const account = String(item?.account_source || '').trim().toLowerCase();
      if (revolutId) params.set('focus_revolut_id', revolutId);
      if (account) params.set('focus_account', account);
      return `/revolut?${params.toString()}`;
    }

    if (kind === 'expense') {
      const params = new URLSearchParams({ tab: 'expenses' });
      const revolutId = String(item?.revolut_id || '').trim();
      const account = String(item?.account_source || '').trim().toLowerCase();
      if (revolutId) params.set('focus_expense_id', revolutId);
      if (account) params.set('focus_account', account);
      return `/revolut?${params.toString()}`;
    }

    const bookingId = String(item?.id ?? item?.booking_id ?? '').trim();
    const externalBookingId = String(item?.external_booking_id || '').trim();
    const icalUid = String(item?.ical_uid || '').trim();

    if (bookingId) return `/bookings/${bookingId}`;

    const bookingParams = new URLSearchParams();
    const source = String(item?.source || '').trim().toLowerCase();
    bookingParams.set('tab', source === 'manual' || source.includes('export') ? 'manual' : 'ical');
    if (externalBookingId) bookingParams.set('focus_external_booking_id', externalBookingId);
    if (icalUid) bookingParams.set('focus_ical_uid', icalUid);

    return `/bookings?${bookingParams.toString()}`;
  };

  useEffect(() => {
    if (!detailModalOpen || !detailModalItem) return;
    const rid = detailModalItem.revolut_id;
    if (!rid || typeof rid !== 'string') return;
    const isExpense = detailModalTitle.includes('Expense');
    const isTransaction = detailModalTitle.includes('Transaction');
    if (!isExpense && !isTransaction) return;

    let cancelled = false;
    setDetailModalLoading(true);

    (async () => {
      try {
        if (isExpense) {
          const r = await api.get('/api/revolut/expenses', { params: { revolut_id: rid } });
          if (cancelled) return;
          const exp = r.data?.expense ?? r.data?.expenses?.[0];
          setDetailModalResolved(exp && typeof exp === 'object' ? exp : null);
        } else {
          const r = await api.get('/api/revolut/transactions/complete', { params: { revolut_id: rid } });
          if (cancelled) return;
          const tx = r.data?.transaction ?? r.data?.transactions?.[0];
          setDetailModalResolved(tx && typeof tx === 'object' ? tx : null);
        }
      } catch {
        if (!cancelled) setDetailModalResolved(null);
      } finally {
        if (!cancelled) setDetailModalLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [detailModalOpen, detailModalItem, detailModalTitle]);

  const {
    latestBankBalances,
    latestMortgageBalances,
    totalBankBalances,
    totalMortgageBalances,
    netWorth,
    assetRows
  } = useMemo(() => {
    let latestBank = accounts.map((account) => ({
      id: account.id,
      account_name: account.account_name,
      balance: Number(account.current_balance || 0),
      currency: 'EUR',
      balanceEur: Number(account.current_balance || 0)
    }));

    // Override with latest synced Revolut transaction balances where available.
    const revolutLatestBySource = new Map<string, RevolutTransaction>();
    for (const tx of revolutTransactions) {
      const source = (tx.account_source || '').toLowerCase();
      if (!source || tx.balance == null || !Number.isFinite(Number(tx.balance))) continue;
      const txTime = new Date(tx.completed_at || tx.created_at_revolut || tx.created_at || 0).getTime();
      const existing = revolutLatestBySource.get(source);
      const existingTime = existing
        ? new Date(existing.completed_at || existing.created_at_revolut || existing.created_at || 0).getTime()
        : -1;
      if (!existing || txTime >= existingTime) {
        revolutLatestBySource.set(source, tx);
      }
    }

    if (revolutLatestBySource.size > 0) {
      const irelandAccount = accounts.find((a) => (a.account_name || '').toLowerCase().includes('ire'));
      const ukAccount = accounts.find((a) => (a.account_name || '').toLowerCase().includes('uk'));
      const irelandTx = revolutLatestBySource.get('ireland');
      const ukTx = revolutLatestBySource.get('uk');

      // These balances come straight from the synced Revolut transactions (the
      // live API), so label them "(API)" rather than the stored "— manual" name.
      const apiLabel = (raw: string | undefined, fallback: string) => {
        const base = (raw || fallback).replace(/\s*[—–-]\s*manual\s*$/i, '').trim();
        return `${base || fallback} (API)`;
      };

      if (irelandTx && irelandTx.balance != null) {
        const accountId = irelandAccount?.id || -101;
        latestBank = latestBank.filter((b) => b.id !== accountId);
        latestBank.push({
          id: accountId,
          account_name: apiLabel(irelandAccount?.account_name, 'Rev Biz Ire'),
          balance: Number(irelandTx.balance),
          currency: 'EUR',
          balanceEur: Number(irelandTx.balance),
        });
      }
      if (ukTx && ukTx.balance != null) {
        const accountId = ukAccount?.id || -102;
        latestBank = latestBank.filter((b) => b.id !== accountId);
        // The UK Revolut account is held in GBP; convert to EUR for the running total.
        latestBank.push({
          id: accountId,
          account_name: apiLabel(ukAccount?.account_name, 'Rev Biz UK'),
          balance: Number(ukTx.balance),
          currency: 'GBP',
          balanceEur: Number(ukTx.balance) * gbpEurRate,
        });
      }
    }

    const latestMortgage = loans
      .filter((loan) => (loan.loan_type || '').toLowerCase() === 'mortgage')
      .map((loan) => ({
        id: loan.id,
        loan_name: loan.loan_name,
        balance: Number(loan.current_balance || 0)
      }));

    const propertyMortgage = new Map<number, number>();
    const mortgageLoans = loans.filter((loan) => (loan.loan_type || '').toLowerCase() === 'mortgage');
    for (const loan of mortgageLoans) {
      const linkedIds = new Set<number>();
      if (Array.isArray(loan.property_ids)) {
        loan.property_ids.forEach((id) => {
          if (Number.isFinite(id)) linkedIds.add(Number(id));
        });
      }
      if (Array.isArray(loan.properties)) {
        loan.properties.forEach((p) => {
          if (p?.id && Number.isFinite(p.id)) linkedIds.add(Number(p.id));
        });
      }
      if (loan.property_id && Number.isFinite(Number(loan.property_id))) {
        linkedIds.add(Number(loan.property_id));
      }

      const linkedCount = linkedIds.size || 1;
      const share = Math.abs(Number(loan.current_balance || 0)) / linkedCount;
      linkedIds.forEach((propertyId) => {
        propertyMortgage.set(propertyId, (propertyMortgage.get(propertyId) || 0) + share);
      });
    }

    const normalizeOwnershipPct = (value: number) => (value > 0 && value <= 1 ? value * 100 : value);

    const assetRows = properties.map((property) => {
      const value = Number(property.valuation || 0);
      const mortgage = Number(propertyMortgage.get(property.id) || 0);
      const equity = value - mortgage;
      const rrLtdPct = normalizeOwnershipPct(Number(property.reinvented_recruitment_ltd_ownership || 0));
      const secodwomPct = normalizeOwnershipPct(Number(property.secodwom_uk_ltd_ownership || 0));
      const omhePct = normalizeOwnershipPct(Number(property.omhe_props_ltd_ownership || 0));
      const omarPct = normalizeOwnershipPct(Number(property.omar_ownership || 0));
      const heidiPct = normalizeOwnershipPct(Number(property.heidi_ownership || 0));
      const seanPct = normalizeOwnershipPct(Number(property.sean_ownership || 0));
      const dwaynePct = normalizeOwnershipPct(Number(property.dwayne_ownership || 0));
      const lenaPct = normalizeOwnershipPct(Number(property.lena_ownership || 0));
      const coralPct = normalizeOwnershipPct(Number(property.coral_ownership || 0));

      return {
        id: property.id,
        nickname: property.nickname,
        value,
        mortgage,
        equity,
        rrLtdPct,
        secodwomPct,
        omhePct,
        omarPct,
        heidiPct,
        seanPct,
        dwaynePct,
        lenaPct,
        coralPct,
        rrLtdEquity: equity * (rrLtdPct / 100),
        secodwomEquity: equity * (secodwomPct / 100),
        omheEquity: equity * (omhePct / 100),
        omarEquity: equity * (omarPct / 100),
        heidiEquity: equity * (heidiPct / 100),
        seanEquity: equity * (seanPct / 100),
        dwayneEquity: equity * (dwaynePct / 100),
        lenaEquity: equity * (lenaPct / 100),
        coralEquity: equity * (coralPct / 100)
      };
    });

    if (assetRows.length === 0) {
      propertyMortgage.forEach((mortgage, propId) => {
        assetRows.push({
          id: propId,
          nickname: `Property ${propId}`,
          value: 0,
          mortgage,
          equity: -mortgage,
          rrLtdPct: 0,
          secodwomPct: 0,
          omhePct: 0,
          omarPct: 0,
          heidiPct: 0,
          seanPct: 0,
          dwaynePct: 0,
          lenaPct: 0,
          coralPct: 0,
          rrLtdEquity: 0,
          secodwomEquity: 0,
          omheEquity: 0,
          omarEquity: 0,
          heidiEquity: 0,
          seanEquity: 0,
          dwayneEquity: 0,
          lenaEquity: 0,
          coralEquity: 0
        });
      });
    }

    const totalBank = latestBank.reduce((sum, entry) => sum + Number(entry.balanceEur || 0), 0);
    const totalMortgage = latestMortgage.reduce((sum, entry) => sum + Math.abs(Number(entry.balance || 0)), 0);
    const totalAssets = assetRows.reduce((sum, row) => sum + Number(row.value || 0), 0);

    return {
      latestBankBalances: latestBank,
      latestMortgageBalances: latestMortgage,
      totalBankBalances: totalBank,
      totalMortgageBalances: totalMortgage,
      netWorth: totalAssets + totalBank - totalMortgage,
      assetRows
    };
  }, [loans, accounts, revolutTransactions, properties, gbpEurRate]);

  /** Sean: same Revolut-synced bank figures as the main dashboard (latest tx balance per IRE/UK). */
  const seanRevBizBankDisplay = useMemo(() => {
    const revolutLatestBySource = new Map<string, RevolutTransaction>();
    for (const tx of revolutTransactions) {
      const source = (tx.account_source || '').toLowerCase();
      if (!source || tx.balance == null || !Number.isFinite(Number(tx.balance))) continue;
      const txTime = new Date(tx.completed_at || tx.created_at_revolut || tx.created_at || 0).getTime();
      const existing = revolutLatestBySource.get(source);
      const existingTime = existing
        ? new Date(existing.completed_at || existing.created_at_revolut || existing.created_at || 0).getTime()
        : -1;
      if (!existing || txTime >= existingTime) {
        revolutLatestBySource.set(source, tx);
      }
    }
    const txTimeIso = (source: 'ireland' | 'uk') => {
      const tx = revolutLatestBySource.get(source);
      if (!tx) return null;
      const t = tx.completed_at || tx.created_at_revolut || tx.created_at;
      return t != null ? String(t) : null;
    };

    const irelandAccount = accounts.find((a) => (a.account_name || '').toLowerCase().includes('ire'));
    const ukAccount = accounts.find((a) => {
      const n = (a.account_name || '').toLowerCase();
      return n.includes('uk') && !n.includes('ire');
    });
    const ireId = irelandAccount?.id ?? -101;
    const ukId = ukAccount?.id ?? -102;
    const ireRow = latestBankBalances.find((b) => b.id === ireId);
    const ukRow = latestBankBalances.find((b) => b.id === ukId);

    const rows: Array<{
      id: number;
      account_name: string;
      currency: string;
      nativeAmount: number;
      eurEquiv: number;
      hasData: boolean;
      retrievedAt: string | null;
    }> = [];
    if (ireRow) {
      rows.push({
        id: ireRow.id,
        account_name: ireRow.account_name,
        currency: 'EUR',
        nativeAmount: Number(ireRow.balance),
        eurEquiv: Number(ireRow.balance),
        hasData: true,
        retrievedAt: txTimeIso('ireland'),
      });
    }
    if (ukRow) {
      // The UK Revolut account is held in GBP; show native GBP plus EUR equivalent.
      rows.push({
        id: ukRow.id,
        account_name: ukRow.account_name,
        currency: 'GBP',
        nativeAmount: Number(ukRow.balance),
        eurEquiv: Number(ukRow.balance) * gbpEurRate,
        hasData: true,
        retrievedAt: txTimeIso('uk'),
      });
    }
    const total = rows.reduce((s, r) => s + r.eurEquiv, 0);
    return { rows, total };
  }, [accounts, latestBankBalances, revolutTransactions, gbpEurRate]);

  const latestRevolutPullAt = useMemo(() => {
    let latestTs = Number.NEGATIVE_INFINITY;
    let latestIso: string | null = null;
    for (const tx of revolutTransactions) {
      const candidate = tx.completed_at || tx.created_at_revolut || tx.created_at;
      if (!candidate) continue;
      const ts = new Date(candidate).getTime();
      if (!Number.isFinite(ts)) continue;
      if (ts > latestTs) {
        latestTs = ts;
        latestIso = String(candidate);
      }
    }
    return latestIso;
  }, [revolutTransactions]);

  /** Bumps when a dismissible reminder is cleared so localStorage is re-read for the list + nav badge. */
  const [reminderEpoch, setReminderEpoch] = useState(0);
  const dashboardReminders = useMemo<DashboardReminder[]>(
    () => buildDashboardReminders(manualReviewSummary, supplierVatPolicyRows, viewerRole),
    [manualReviewSummary, supplierVatPolicyRows, reminderEpoch, viewerRole]
  );

  /** VAT / supplier reminders only — TX backlog is rendered separately so it never disappears when this list is empty. */
  const vatAndOtherReminders = useMemo(
    () => dashboardReminders.filter((r) => r.key !== 'review-queue-open'),
    [dashboardReminders]
  );

  const pendingClassificationCount = manualReviewSummary?.manual_reviews_required ?? 0;
  const hasPendingClassification = pendingClassificationCount > 0;

  useEffect(() => {
    window.dispatchEvent(new CustomEvent(DASHBOARD_REMINDERS_CHANGED_EVENT));
  }, [manualReviewSummary, supplierVatPolicyRows]);

  useEffect(() => {
    let cancelled = false;
    const hydrateReminderDismissal = async () => {
      const dbValue = await getUserUiState('dashboard.reminder.post_deploy');
      if (cancelled || !dbValue) return;
      if (dbValue.dismissed === true) {
        try {
          localStorage.setItem(POST_DEPLOY_MIGRATIONS_REMINDER_DISMISS_KEY, '1');
        } catch {
          // ignore localStorage failures; DB remains source of truth
        }
        setReminderEpoch((n) => n + 1);
        window.dispatchEvent(new CustomEvent(DASHBOARD_REMINDERS_CHANGED_EVENT));
      }
    };
    void hydrateReminderDismissal();
    return () => {
      cancelled = true;
    };
  }, []);

  const totals = useMemo(() => {
    return assetRows.reduce(
      (acc, row) => ({
        value: acc.value + row.value,
        mortgage: acc.mortgage + row.mortgage,
        equity: acc.equity + row.equity,
        rrLtdEquity: acc.rrLtdEquity + row.rrLtdEquity,
        secodwomEquity: acc.secodwomEquity + row.secodwomEquity,
        omheEquity: acc.omheEquity + row.omheEquity,
        omarEquity: acc.omarEquity + row.omarEquity,
        heidiEquity: acc.heidiEquity + row.heidiEquity,
        seanEquity: acc.seanEquity + row.seanEquity,
        dwayneEquity: acc.dwayneEquity + row.dwayneEquity,
        lenaEquity: acc.lenaEquity + row.lenaEquity,
        coralEquity: acc.coralEquity + row.coralEquity
      }),
      {
        value: 0,
        mortgage: 0,
        equity: 0,
        rrLtdEquity: 0,
        secodwomEquity: 0,
        omheEquity: 0,
        omarEquity: 0,
        heidiEquity: 0,
        seanEquity: 0,
        dwayneEquity: 0,
        lenaEquity: 0,
        coralEquity: 0
      }
    );
  }, [assetRows]);

  const familyTotals = useMemo(() => {
    const omarHeidi = totals.omarEquity + totals.heidiEquity;
    const seanCoral = totals.seanEquity + totals.coralEquity;
    const dwayneLena = totals.dwayneEquity + totals.lenaEquity;
    return {
      omheEquity: totals.omheEquity,
      rrLtdEquity: totals.rrLtdEquity,
      secodwomEquity: totals.secodwomEquity,
      omarEquity: omarHeidi,
      heidiEquity: 0,
      seanEquity: seanCoral,
      coralEquity: 0,
      dwayneEquity: dwayneLena,
      lenaEquity: 0
    };
  }, [totals]);

  const formatLtv = (mortgage: number, value: number) => {
    const safeValue = Number(value || 0);
    if (safeValue <= 0) return '0.0%';
    const ltv = (Number(mortgage || 0) / safeValue) * 100;
    return `${ltv.toFixed(1)}%`;
  };

  const familyColumnStyles: Record<string, { headerBg: string; bodyBg: string; text: string }> = {
    rrltd: { headerBg: '#fff8e1', bodyBg: '#fffdf2', text: '#8a6d00' },
    secodwom: { headerBg: '#f3e5f5', bodyBg: '#faf3fc', text: '#6a1b9a' },
    omhe: { headerBg: '#ede7f6', bodyBg: '#f5f2fc', text: '#4527a0' },
    omar: { headerBg: '#e3f2fd', bodyBg: '#f1f8ff', text: '#0d47a1' },
    heidi: { headerBg: '#e3f2fd', bodyBg: '#f1f8ff', text: '#0d47a1' },
    dwayne: { headerBg: '#e8f5e9', bodyBg: '#f1fbf3', text: '#1b5e20' },
    lena: { headerBg: '#e8f5e9', bodyBg: '#f1fbf3', text: '#1b5e20' },
    sean: { headerBg: '#f3e5f5', bodyBg: '#faf2fc', text: '#4a148c' },
    coral: { headerBg: '#f3e5f5', bodyBg: '#faf2fc', text: '#4a148c' }
  };

  const mqXl = useMediaQuery(theme.breakpoints.up('xl'));
  const mqLg = useMediaQuery(theme.breakpoints.up('lg'));
  const mqMd = useMediaQuery(theme.breakpoints.up('md'));
  const mqSm = useMediaQuery(theme.breakpoints.up('sm'));

  /**
   * Keep responsive hiding group-aware:
   * - families are shown/hidden as full pairs
   * - businesses can be hidden one column at a time on the smallest screens
   * Rightmost groups are hidden first as width shrinks.
   */
  const visibleGroupCount = mqXl ? 6 : mqLg ? 5 : mqMd ? 4 : mqSm ? 3 : 2;

  const visibleEquityKeys = useMemo(() => {
    const groupedKeys: EquityColKey[][] = [
      ['omhe'],
      ['rrltd'],
      ['secodwom'],
      ['omar', 'heidi'],
      ['dwayne', 'lena'],
      ['sean', 'coral']
    ];
    return groupedKeys.slice(0, visibleGroupCount).flat();
  }, [visibleGroupCount]);
  const equityBreakdownColSpan = 4 + visibleEquityKeys.length;

  const wealthTrajectory = useMemo(() => {
    const monthCount = 12;
    const now = new Date();
    const toMonthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const monthStarts = Array.from({ length: monthCount }, (_, i) => {
      return new Date(now.getFullYear(), now.getMonth() - (monthCount - 1 - i), 1);
    });
    const monthKeys = monthStarts.map(toMonthKey);

    const baseAssets = Number(totals.value || 0);
    const currentLoanBalanceById = new Map<number, number>();
    for (const loan of loans) {
      if (!Number.isFinite(Number(loan.id))) continue;
      currentLoanBalanceById.set(Number(loan.id), Math.abs(Number(loan.current_balance || 0)));
    }

    const bySource = new Map<string, Array<{ at: Date; balance: number }>>();

    for (const tx of revolutTransactions) {
      const source = (tx.account_source || '').toLowerCase().trim();
      const txBalance = Number(tx.balance);
      if (!source || !Number.isFinite(txBalance)) continue;
      const txTime = new Date(tx.completed_at || tx.created_at_revolut || tx.created_at || 0);
      if (Number.isNaN(txTime.getTime())) continue;
      const entries = bySource.get(source) || [];
      entries.push({ at: txTime, balance: txBalance });
      bySource.set(source, entries);
    }

    bySource.forEach((entries, source) => {
      const ordered = [...entries].sort((a, b) => a.at.getTime() - b.at.getTime());
      bySource.set(source, ordered);
    });

    const mortgageByLoan = new Map<number, Array<{ at: Date; balance: number }>>();
    for (const entry of loanBalanceHistory) {
      const loanId = Number(entry.loan_id);
      const rawBalance = Number(entry.balance);
      const enteredAt = new Date(entry.date_entered || 0);
      if (!Number.isFinite(loanId) || !Number.isFinite(rawBalance) || Number.isNaN(enteredAt.getTime())) continue;
      const items = mortgageByLoan.get(loanId) || [];
      items.push({ at: enteredAt, balance: Math.abs(rawBalance) });
      mortgageByLoan.set(loanId, items);
    }
    mortgageByLoan.forEach((entries, loanId) => {
      mortgageByLoan.set(loanId, [...entries].sort((a, b) => a.at.getTime() - b.at.getTime()));
    });

    const historicalLoanIds = new Set<number>([
      ...Array.from(currentLoanBalanceById.keys()),
      ...Array.from(mortgageByLoan.keys())
    ]);

    const series = monthKeys.map((_, index) => {
      let monthBankTotal = 0;
      let monthMortgageTotal = 0;
      const hasSourceData = bySource.size > 0;
      if (hasSourceData) {
        const monthStart = monthStarts[index];
        const nextMonthStart = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1);
        const sources = Array.from(bySource.keys());
        for (const source of sources) {
          const entries = bySource.get(source) || [];
          let resolvedBalance = 0;

          // Prefer opening-month balance: first transaction in the month.
          const firstInMonth = entries.find((entry) => entry.at >= monthStart && entry.at < nextMonthStart);
          if (firstInMonth) {
            resolvedBalance = Number(firstInMonth.balance);
          } else {
            // If no transaction this month, carry forward the latest known prior balance.
            for (let j = entries.length - 1; j >= 0; j -= 1) {
              if (entries[j].at < monthStart) {
                resolvedBalance = Number(entries[j].balance);
                break;
              }
            }
          }

          monthBankTotal += resolvedBalance;
        }
      } else {
        monthBankTotal = Number(totalBankBalances || 0);
      }

      const monthStart = monthStarts[index];
      const nextMonthStart = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1);
      historicalLoanIds.forEach((loanId) => {
        const entries = mortgageByLoan.get(loanId) || [];
        let resolvedBalance = 0;
        const firstInMonth = entries.find((entry) => entry.at >= monthStart && entry.at < nextMonthStart);
        if (firstInMonth) {
          resolvedBalance = Number(firstInMonth.balance);
        } else {
          for (let j = entries.length - 1; j >= 0; j -= 1) {
            if (entries[j].at < monthStart) {
              resolvedBalance = Number(entries[j].balance);
              break;
            }
          }
          if (!resolvedBalance) {
            resolvedBalance = Number(currentLoanBalanceById.get(loanId) || 0);
          }
        }
        monthMortgageTotal += resolvedBalance;
      });

      const netWorthValue = baseAssets + monthBankTotal - monthMortgageTotal;
      return {
        month: monthStarts[index].toLocaleDateString(undefined, { month: 'short', year: '2-digit' }),
        assets: baseAssets,
        bank: monthBankTotal,
        mortgages: -monthMortgageTotal,
        netWorth: netWorthValue
      };
    });

    const first = series[0]?.netWorth ?? 0;
    const last = series[series.length - 1]?.netWorth ?? 0;
    return {
      points: series,
      change12m: last - first
    };
  }, [revolutTransactions, loanBalanceHistory, loans, totals.value, totalBankBalances]);

  const allCronRuns = useMemo(() => {
    if (cronConfig?.recent_runs && cronConfig.recent_runs.length > 0) {
      return cronConfig.recent_runs;
    }
    if (cronConfig?.last_run_at) {
      return [{
        id: -1,
        run_at: cronConfig.last_run_at,
        status: cronConfig.last_run_status || 'unknown',
        message: cronConfig.last_run_message || null,
        details: cronConfig.last_run_details || null
      }];
    }
    return [];
  }, [cronConfig]);

  const visibleCronRuns = useMemo(() => {
    const limit = showMoreCronRuns ? EXPANDED_CRON_RUNS_COUNT : DEFAULT_CRON_RUNS_COUNT;
    return allCronRuns.slice(0, limit);
  }, [allCronRuns, showMoreCronRuns, DEFAULT_CRON_RUNS_COUNT, EXPANDED_CRON_RUNS_COUNT]);

  const canToggleCronRuns = allCronRuns.length > DEFAULT_CRON_RUNS_COUNT;

  const toggleCronRunsView = () => {
    // Avoid a hidden expanded row when collapsing back to the compact view.
    setExpandedRunId(false);
    setShowMoreCronRuns((prev) => !prev);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 0, mb: 2 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
          <DashboardIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Dashboard
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Snackbar
          open={unavailableToastOpen}
          autoHideDuration={15000}
          onClose={(_event, reason) => {
            if (reason === 'clickaway') return;
            setUnavailableToastOpen(false);
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            severity="warning"
            variant="filled"
            onClose={() => setUnavailableToastOpen(false)}
            sx={{ maxWidth: 440 }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: unavailableData.length ? 0.5 : 0 }}>
              Some dashboard data is currently unavailable
            </Typography>
            {unavailableData.length ? (
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {unavailableData.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </Box>
            ) : null}
          </Alert>
        </Snackbar>
        {infoMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfoMessage('')}>
            {infoMessage}
          </Alert>
        )}

        {userEmail ? (
          <Box sx={{ mb: 2 }}>
            <RecurringTasksCard />
          </Box>
        ) : null}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : isSeanDashboard ? (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 8, md: 6 }}>
              <CollapsiblePanel
                id="bank-account-balances"
                icon={<AccountBalanceIcon color="primary" />}
                title="Bank Account Balances"
                actions={
                  <Typography variant="caption" color="text.secondary">
                    Pulled: {latestRevolutPullAt ? formatDateTime(latestRevolutPullAt) : 'n/a'}
                  </Typography>
                }
              >
                  {seanRevBizBankDisplay.rows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Rev Biz Ire / Rev Biz UK accounts not found
                    </Typography>
                  ) : (
                    seanRevBizBankDisplay.rows.map((entry) => (
                      <Box
                        key={entry.id}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'baseline',
                          gap: 2,
                          py: 0.4,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
                          <Typography variant="body2" component="span">
                            {entry.account_name || 'Account'}
                          </Typography>
                          {entry.hasData ? (
                            entry.currency === 'GBP' ? (
                              <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                                {formatMoney(entry.nativeAmount, 'GBP')}
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75, fontWeight: 400 }}>
                                  ≈ {formatCurrency(entry.eurEquiv)}
                                </Typography>
                              </Typography>
                            ) : (
                              <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                                {formatCurrency(entry.eurEquiv)}
                              </Typography>
                            )
                          ) : (
                            <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                              —
                            </Typography>
                          )}
                        </Box>
                        {entry.hasData && entry.retrievedAt ? (
                          <Typography
                            variant="caption"
                            component="span"
                            sx={{ color: 'text.secondary', flexShrink: 0, opacity: 0.85 }}
                          >
                            ({formatDateTime(entry.retrievedAt)})
                          </Typography>
                        ) : null}
                      </Box>
                    ))
                  )}
                  {seanRevBizBankDisplay.rows.some((e) => e.currency === 'GBP') ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      UK converted at GBP→EUR {gbpEurRate.toFixed(4)}
                      {gbpEurRateDate ? ` (ECB ${gbpEurRateDate})` : ''}
                    </Typography>
                  ) : null}
              </CollapsiblePanel>
            </Grid>
          </Grid>
        ) : isPersonalAccountsDashboard ? (
          <Grid container spacing={2}>
            {personalSummaries.length === 0 ? (
              <Grid size={{ xs: 12 }}>
                <Alert severity="info">No income & spending summary yet for this household.</Alert>
              </Grid>
            ) : null}
            {personalSummaries.map((household) => {
                const months = household.months || [];
                const mix = personalMixRows(household.spend);
                const latest = [...months].reverse().find((row) => row.income || row.expenses) || months[months.length - 1];
                return (
                  <React.Fragment key={household.household}>
                    <Grid size={{ xs: 12 }}>
                      <CollapsiblePanel
                        data-testid="personal-accounts-summary"
                        icon={<TrendingUpIcon color="primary" />}
                        title={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6">Income & spending</Typography>
                            <Chip size="small" label={household.label} />
                          </Box>
                        }
                        actions={
                          latest ? (
                            <Typography variant="caption" color="text.secondary">
                              {latest.label} net {formatCurrency(latest.net)}
                            </Typography>
                          ) : undefined
                        }
                      >
                          <PersonalCashflowHistory
                            months={months}
                            household={household.household}
                            formatCurrency={formatCurrency}
                            onView={navigate}
                          />
                          <Button
                            size="small"
                            sx={{ mt: 0.5 }}
                            onClick={() => navigate(`/personal-finances?tab=${household.household}`)}
                          >
                            Open Personal Finances
                          </Button>
                      </CollapsiblePanel>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <CollapsiblePanel
                        data-testid="personal-spend-mix"
                        icon={<BarChartIcon color="primary" />}
                        title={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6">Share of income (last 12 months)</Typography>
                            <Chip size="small" label={household.label} />
                          </Box>
                        }
                      >
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            Of income. Totals can exceed 100% when spending exceeds income. Savings is money set
                            aside, not withdrawals. Pension is counted under Savings.
                          </Typography>
                          <Stack spacing={2}>
                            {mix.map((row) => (
                              <Box key={row.pillar}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    {row.label}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
                                    {formatPersonalSpendPct(row.pct)}{' '}
                                    <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                                      {formatCurrency(row.amount)}
                                    </Box>
                                  </Typography>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.max(0, Math.min(100, Number(row.pct || 0)))}
                                  sx={{
                                    height: 8,
                                    borderRadius: 1,
                                    bgcolor: 'action.hover',
                                    '& .MuiLinearProgress-bar': { bgcolor: PERSONAL_MIX_COLORS[row.pillar] },
                                  }}
                                />
                                {row.parts.length === 0 ? (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    None in the last 12 months
                                  </Typography>
                                ) : (
                                  <Stack spacing={0.25} sx={{ mt: 0.75, pl: 0.5 }}>
                                    {row.parts.map((part) => (
                                      <Box
                                        key={part.part}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          gap: 1,
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                          <Typography variant="caption" color="text.secondary">
                                            {part.label}
                                          </Typography>
                                          <Button
                                            size="small"
                                            onClick={() =>
                                              navigate(personalSpendPartPath(household.household, part.part))
                                            }
                                            aria-label={`View ${part.label}`}
                                            sx={PERSONAL_VIEW_BUTTON_SX}
                                          >
                                            View
                                          </Button>
                                        </Box>
                                        <Typography variant="caption" sx={{ flexShrink: 0 }}>
                                          {formatPersonalSpendPct(part.pct)}{' '}
                                          <Box component="span" sx={{ color: 'text.secondary' }}>
                                            {formatCurrency(part.amount)}
                                          </Box>
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Stack>
                                )}
                              </Box>
                            ))}
                          </Stack>
                      </CollapsiblePanel>
                    </Grid>
                  </React.Fragment>
                );
              })}
          </Grid>
        ) : !isOmarDashboard ? (
          <Alert severity="info">
            This home dashboard is not set up for your account. Use the menu to open pages you have access to.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }} sx={{ order: 6 }}>
              <CollapsiblePanel icon={<TrendingUpIcon color="primary" />} title="Net Worth">
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {formatCurrency(netWorth)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Asset values + bank balances - mortgage balances
                  </Typography>
              </CollapsiblePanel>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }} sx={{ order: 4 }}>
              <CollapsiblePanel
                id="bank-account-balances"
                icon={<AccountBalanceIcon color="primary" />}
                title="Bank Account Balances"
                actions={
                  <Typography variant="caption" color="text.secondary">
                    Pulled: {latestRevolutPullAt ? formatDateTime(latestRevolutPullAt) : 'n/a'}
                  </Typography>
                }
              >
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    {formatCurrency(totalBankBalances)}
                  </Typography>
                  {latestBankBalances.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No bank balances yet</Typography>
                  ) : (
                    latestBankBalances.map((entry) => {
                      const isGbp = entry.currency === 'GBP';
                      return (
                        <Box key={entry.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4, gap: 2 }}>
                          <Typography variant="body2">{entry.account_name || 'Account'}</Typography>
                          {isGbp ? (
                            <Typography variant="body2" sx={{ textAlign: 'right' }}>
                              {formatMoney(Number(entry.balance || 0), 'GBP')}
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                                ≈ {formatCurrency(Number(entry.balanceEur || 0))}
                              </Typography>
                            </Typography>
                          ) : (
                            <Typography variant="body2">{formatCurrency(Number(entry.balance || 0))}</Typography>
                          )}
                        </Box>
                      );
                    })
                  )}
                  {latestBankBalances.some((e) => e.currency === 'GBP') ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      UK converted at GBP→EUR {gbpEurRate.toFixed(4)}
                      {gbpEurRateDate ? ` (ECB ${gbpEurRateDate})` : ''}
                    </Typography>
                  ) : null}
              </CollapsiblePanel>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }} sx={{ order: 5 }}>
              <CollapsiblePanel icon={<HomeWorkIcon color="primary" />} title="Mortgage Balances">
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    {formatCurrency(totalMortgageBalances)}
                  </Typography>
                  {latestMortgageBalances.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No mortgage balances yet</Typography>
                  ) : (
                    latestMortgageBalances.map((entry) => {
                      return (
                        <Box key={entry.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
                          <Typography variant="body2">{entry.loan_name || 'Mortgage'}</Typography>
                          <Typography variant="body2">
                            {formatCurrency(Math.abs(Number(entry.balance || 0)))}
                          </Typography>
                        </Box>
                      );
                    })
                  )}
              </CollapsiblePanel>
            </Grid>

            <Grid size={{ xs: 12 }} sx={{ order: 7 }}>
              <CollapsiblePanel icon={<TrendingUpIcon color="primary" />} title="Wealth Change (Last 12 Months)">
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {formatCurrency(wealthTrajectory.change12m)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Includes assets, bank balances, and mortgages.
                  </Typography>

                  <Box sx={{ width: '100%', height: 320 }}>
                    <ResponsiveContainer>
                      <ComposedChart data={wealthTrajectory.points}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={(v) => new Intl.NumberFormat(undefined, { notation: 'compact' }).format(v)} />
                        <Tooltip formatter={(v: any) => formatCurrency(Number(v || 0))} />
                        <Legend />
                        <Bar dataKey="assets" stackId="wealth" fill="#64b5f6" name="Assets" />
                        <Bar dataKey="bank" stackId="wealth" fill="#81c784" name="Bank" />
                        <Bar dataKey="mortgages" stackId="wealth" fill="#ef9a9a" name="Mortgages (-)" />
                        <Line type="monotone" dataKey="netWorth" stroke="#1a237e" strokeWidth={2.5} dot={{ r: 2 }} name="Net Worth" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
              </CollapsiblePanel>
            </Grid>

            <Grid size={{ xs: 12 }} sx={{ order: 8 }}>
              <CollapsiblePanel icon={<BusinessIcon color="primary" />} title="Assets & Equity Breakdown">
                  {visibleEquityKeys.length < EQUITY_COL_ORDER.length && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Showing {visibleEquityKeys.length} of {EQUITY_COL_ORDER.length} equity columns — wider screen shows more (rightmost columns hidden first).
                    </Typography>
                  )}
                  <TableContainer sx={{ ...FIT_TABLE_CONTAINER_SX, WebkitOverflowScrolling: 'touch' }}>
                    <Table
                      size="small"
                      sx={{
                        ...FIT_TABLE_SX,
                        '& .MuiTableCell-root': {
                          ...FIT_TABLE_SX['& .MuiTableCell-root'],
                          fontSize: { xs: '0.72rem', sm: '0.8125rem' },
                          lineHeight: 1.2,
                          verticalAlign: 'top'
                        },
                        '& th': {
                          whiteSpace: 'normal',
                          fontWeight: 700
                        }
                      }}
                    >
                      <FitColgroup
                        cols={[
                          { key: 'property', pct: 14 },
                          ...['value', 'mortgage', 'equity', ...visibleEquityKeys].map((key) => ({
                            key,
                            pct: Number((86 / (3 + visibleEquityKeys.length)).toFixed(3))
                          }))
                        ]}
                      />
                      <TableHead>
                        <TableRow>
                          <TableCell align="center">Property</TableCell>
                          <TableCell align="center">Property Value</TableCell>
                          <TableCell align="center">Mortgage</TableCell>
                          <TableCell align="center">Equity<br />LTV %</TableCell>
                          {visibleEquityKeys.map((key) => (
                            <TableCell
                              key={key}
                              align="center"
                              sx={{
                                bgcolor: familyColumnStyles[key].headerBg,
                                color: familyColumnStyles[key].text
                              }}
                            >
                              {EQUITY_COL_LABELS[key]}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {assetRows.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={equityBreakdownColSpan}>No asset rows available.</TableCell>
                          </TableRow>
                        ) : (
                          assetRows.map((row) => (
                            <TableRow key={row.id}>
                              <TableCell>
                                <Box component="span" sx={fitTruncateSx} title={row.nickname}>
                                  {row.nickname}
                                </Box>
                              </TableCell>
                              <TableCell align="right">{formatCurrency(row.value)}</TableCell>
                              <TableCell align="right">{formatCurrency(row.mortgage)}</TableCell>
                              <TableCell align="right">
                                {formatCurrency(row.equity)}
                                <br />
                                <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                                  {formatLtv(row.mortgage, row.value)}
                                </Box>
                              </TableCell>
                              {visibleEquityKeys.map((key) => {
                                const { eur, pct } = equitySliceForDataRow(row, key);
                                return (
                                  <TableCell key={key} align="right" sx={{ bgcolor: familyColumnStyles[key].bodyBg }}>
                                    {formatCurrency(eur)}
                                    <br />
                                    <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                                      {pct.toFixed(1)}%
                                    </Box>
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))
                        )}
                        {assetRows.length > 0 && (
                          <>
                          <TableRow sx={{ '& td': { fontWeight: 700 } }}>
                            <TableCell>
                              <Box component="span" sx={fitTruncateSx}>Total (Individual)</Box>
                            </TableCell>
                            <TableCell align="right">{formatCurrency(totals.value)}</TableCell>
                            <TableCell align="right">{formatCurrency(totals.mortgage)}</TableCell>
                            <TableCell align="right">
                              {formatCurrency(totals.equity)}
                              <br />
                              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                                {formatLtv(totals.mortgage, totals.value)}
                              </Box>
                            </TableCell>
                            {visibleEquityKeys.map((key) => (
                              <TableCell key={key} align="right" sx={{ bgcolor: familyColumnStyles[key].bodyBg }}>
                                {formatCurrency(equityAmountForTotalRow(totals, key))}
                              </TableCell>
                            ))}
                          </TableRow>
                          <TableRow sx={{ '& td': { fontWeight: 700 } }}>
                            <TableCell>
                              <Box component="span" sx={fitTruncateSx}>Total (Family)</Box>
                            </TableCell>
                            <TableCell align="right">{formatCurrency(totals.value)}</TableCell>
                            <TableCell align="right">{formatCurrency(totals.mortgage)}</TableCell>
                            <TableCell align="right">
                              {formatCurrency(totals.equity)}
                              <br />
                              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                                {formatLtv(totals.mortgage, totals.value)}
                              </Box>
                            </TableCell>
                            {visibleEquityKeys.map((key) => (
                              <TableCell key={key} align="right" sx={{ bgcolor: familyColumnStyles[key].bodyBg }}>
                                {equityAmountForTotalRow(familyTotals, key) !== 0
                                  ? formatCurrency(equityAmountForTotalRow(familyTotals, key))
                                  : ''}
                              </TableCell>
                            ))}
                          </TableRow>
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
              </CollapsiblePanel>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }} sx={{ order: 1, alignSelf: 'flex-start' }}>
              <CollapsiblePanel
                variant="outlined"
                sx={{ borderColor: 'warning.light' }}
                icon={<NotificationsActiveIcon color="warning" />}
                title="Reminders"
              >
                  {!hasPendingClassification && vatAndOtherReminders.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No reminders yet. Pending transaction reviews, VAT lookback due dates, post-deploy maintenance
                      notes, and other alerts show here when applicable.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {hasPendingClassification && (
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'warning.light',
                            bgcolor: 'warning.50',
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Transaction classification reviews pending
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, my: 0.75 }}>
                            {pendingClassificationCount}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Untagged: {manualReviewSummary?.untagged_transactions ?? 0} | VAT conflicts:{' '}
                            {manualReviewSummary?.conflicting_vat_transactions ?? 0}
                          </Typography>
                          <Box sx={{ mt: 1.5 }}>
                            <Button size="small" variant="outlined" href="/transaction-classification-review">
                              Open review page
                            </Button>
                          </Box>
                        </Box>
                      )}
                      {vatAndOtherReminders.map((reminder) => (
                        <Box
                          key={reminder.key}
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor:
                              reminder.severity === 'error'
                                ? 'error.light'
                                : reminder.severity === 'warning'
                                  ? 'warning.light'
                                  : 'info.light',
                            bgcolor:
                              reminder.severity === 'error'
                                ? 'error.50'
                                : reminder.severity === 'warning'
                                  ? 'warning.50'
                                  : 'info.50',
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {reminder.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {reminder.detail}
                          </Typography>
                          {reminder.dismissStorageKey ? (
                            <Box sx={{ mt: 1 }}>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                  try {
                                    localStorage.setItem(reminder.dismissStorageKey!, '1');
                                  } catch {
                                    /* ignore quota / private mode */
                                  }
                                  void putUserUiState(
                                    'dashboard.reminder.post_deploy',
                                    { dismissed: true, dismissed_at: new Date().toISOString() },
                                    'localStorage',
                                    true
                                  );
                                  setReminderEpoch((n) => n + 1);
                                  window.dispatchEvent(new CustomEvent(DASHBOARD_REMINDERS_CHANGED_EVENT));
                                }}
                              >
                                Dismiss
                              </Button>
                            </Box>
                          ) : null}
                        </Box>
                      ))}
                    </Stack>
                  )}
              </CollapsiblePanel>
            </Grid>

            <Grid size={{ xs: 12 }} sx={{ order: 3 }}>
              <CollapsiblePanel
                icon={<FlightTakeoffIcon color="primary" />}
                title={"This Week's Stays & Flights"}
                actions={
                  <Button size="small" href="/bookings">
                    Manage bookings
                  </Button>
                }
              >
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {formatBookingDate(dateToLocalIso(weekStayGroups.weekStart))}
                    {' – '}
                    {formatBookingDate(dateToLocalIso(weekStayGroups.weekEnd))}
                    . Click a row to view the booking.
                  </Typography>
                  {weekStayGroups.currentStays.length === 0 && weekStayGroups.upcomingStays.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No stays this week.
                    </Typography>
                  ) : (
                    <Stack spacing={3}>
                      {([
                        { title: 'Current Stays', rows: weekStayGroups.currentStays, emptyText: 'No guests currently in residence this week.' },
                        { title: 'Upcoming Stays', rows: weekStayGroups.upcomingStays, emptyText: 'No check-ins remaining this week.' },
                      ] as const).map(({ title, rows, emptyText }) => (
                        <Box key={title}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            {title}
                          </Typography>
                          {rows.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {emptyText}
                            </Typography>
                          ) : (
                            <TableContainer sx={FIT_TABLE_CONTAINER_SX}>
                              <Table size="small" sx={FIT_TABLE_SX}>
                                <FitColgroup
                                  cols={[
                                    { key: 'guest' },
                                    { key: 'property', pct: 16 },
                                    { key: 'check_in', pct: 12 },
                                    { key: 'check_out', pct: 12 },
                                    { key: 'flight_in', pct: 19 },
                                    { key: 'flight_out', pct: 19 },
                                  ]}
                                />
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Guest</TableCell>
                                    <TableCell>Property</TableCell>
                                    <TableCell>Check-in</TableCell>
                                    <TableCell>Check-out</TableCell>
                                    <TableCell>Flight IN</TableCell>
                                    <TableCell>Flight OUT</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {rows.map((booking) => (
                                      <TableRow
                                        key={booking.id}
                                        hover
                                        onClick={() => openBookingOnBookingsPage(booking)}
                                        sx={{ cursor: 'pointer' }}
                                      >
                                        <TableCell>
                                          <Box
                                            component="span"
                                            sx={fitTruncateSx}
                                            title={booking.guest_name || 'Guest'}
                                          >
                                            {booking.guest_name || 'Guest'}
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          <Box
                                            component="span"
                                            sx={fitTruncateSx}
                                            title={booking.property_nickname || ''}
                                          >
                                            {booking.property_nickname || '—'}
                                          </Box>
                                        </TableCell>
                                        <TableCell>{formatBookingDate(booking.check_in_date)}</TableCell>
                                        <TableCell>{formatBookingDate(booking.check_out_date)}</TableCell>
                                        <TableCell>
                                          {renderFlightCell(
                                            booking.flight_in,
                                            booking.flight_in_display,
                                            booking.flight_in_status_display
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {renderFlightCell(
                                            booking.flight_out,
                                            booking.flight_out_display,
                                            booking.flight_out_status_display
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  )}
              </CollapsiblePanel>
            </Grid>

            <Grid size={{ xs: 12 }} sx={{ order: 2 }}>
              <CollapsiblePanel
                id="monthly-performance"
                icon={<TrendingUpIcon color="primary" />}
                title="Monthly Performance vs History"
                actions={
                  <Button size="small" href="/bookings">
                    Manage bookings
                  </Button>
                }
              >
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {monthlyStats
                      ? `${new Date(monthlyStats.year, monthlyStats.month - 1, 1).toLocaleString(undefined, { month: 'long' })} ${monthlyStats.year}`
                      : 'This month'}
                    {' — this month vs the average of the same month in previous years.'}
                  </Typography>
                  {!monthlyStats || monthlyStats.properties.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No booking history available yet.
                    </Typography>
                  ) : (
                    <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Property</TableCell>
                            <TableCell align="right">Occupancy</TableCell>
                            <TableCell align="right">vs history</TableCell>
                            <TableCell align="right">Avg €/night</TableCell>
                            <TableCell align="right">vs history</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {monthlyStats.properties.map((p) => {
                            const occDelta = p.delta.occupancy_pct;
                            const occGood = occDelta == null ? 0 : occDelta;
                            const priceDelta = p.delta.avg_price_night;
                            const priceRel = p.delta.avg_price_night_rel;
                            const priceGood = priceDelta == null ? 0 : priceDelta;
                            return (
                              <TableRow key={p.property_id}>
                                <TableCell sx={{ fontWeight: 600 }}>{p.nickname}</TableCell>
                                <TableCell align="right">
                                  {p.current.occupancy_pct.toFixed(0)}%
                                  <Box component="span" sx={{ color: 'text.secondary', display: 'block', fontSize: 11 }}>
                                    {p.current.booked_nights}/{p.current.days_in_month} nights
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  {p.baseline.occupancy_pct == null || occDelta == null ? (
                                    <Typography variant="caption" color="text.secondary">no history</Typography>
                                  ) : (
                                    <>
                                      <Chip
                                        size="small"
                                        color={occGood > 0 ? 'success' : occGood < 0 ? 'error' : 'default'}
                                        label={`${occGood > 0 ? '+' : ''}${occDelta.toFixed(1)} pts`}
                                      />
                                      <Box component="span" sx={{ color: 'text.secondary', display: 'block', fontSize: 11 }}>
                                        avg {p.baseline.occupancy_pct.toFixed(0)}% · {p.baseline.years_count} yr{p.baseline.years_count === 1 ? '' : 's'}
                                      </Box>
                                    </>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {p.current.avg_price_night == null ? '—' : formatCurrency(p.current.avg_price_night)}
                                </TableCell>
                                <TableCell align="right">
                                  {p.baseline.avg_price_night == null || priceDelta == null ? (
                                    <Typography variant="caption" color="text.secondary">no history</Typography>
                                  ) : (
                                    <>
                                      <Chip
                                        size="small"
                                        color={priceGood > 0 ? 'success' : priceGood < 0 ? 'error' : 'default'}
                                        label={`${priceDelta > 0 ? '+' : ''}${formatCurrency(priceDelta)}${priceRel != null ? ` (${priceRel > 0 ? '+' : ''}${priceRel.toFixed(0)}%)` : ''}`}
                                      />
                                      <Box component="span" sx={{ color: 'text.secondary', display: 'block', fontSize: 11 }}>
                                        avg {formatCurrency(p.baseline.avg_price_night)}
                                      </Box>
                                    </>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {monthlyStats && monthlyStats.properties.length > 0 && (() => {
                    const selectedId = statsChartPropId ?? monthlyStats.properties[0].property_id;
                    const selected = monthlyStats.properties.find((p) => p.property_id === selectedId) || monthlyStats.properties[0];
                    // Least-squares linear regression over indexed points; y may be
                    // null (e.g. idle months have no price) and those points are skipped.
                    const linFit = (ys: Array<number | null>) => {
                      const pts = ys
                        .map((y, i) => ({ x: i, y }))
                        .filter((p): p is { x: number; y: number } => p.y != null);
                      const n = pts.length;
                      if (n < 2) return null;
                      const sx = pts.reduce((a, p) => a + p.x, 0);
                      const sy = pts.reduce((a, p) => a + p.y, 0);
                      const sxx = pts.reduce((a, p) => a + p.x * p.x, 0);
                      const sxy = pts.reduce((a, p) => a + p.x * p.y, 0);
                      const denom = n * sxx - sx * sx;
                      if (denom === 0) return null;
                      const slope = (n * sxy - sx * sy) / denom;
                      const intercept = (sy - slope * sx) / n;
                      return { slope, intercept };
                    };
                    // Only complete calendar months count for occupancy: the current
                    // month is still in progress and future months have no data yet, so
                    // both the fit and the drawn line stop at the last complete month.
                    const now = new Date();
                    const curY = now.getFullYear();
                    const curM = now.getMonth() + 1;
                    const isComplete = (s: { year: number; month: number }) =>
                      s.year < curY || (s.year === curY && s.month < curM);
                    const occFit = linFit(selected.series.map((s) => (isComplete(s) ? s.occupancy_pct : null)));
                    const priceFit = linFit(selected.series.map((s) => s.avg_price_night));
                    const chartData = selected.series.map((s, i) => ({
                      label: s.label,
                      price: s.avg_price_night,
                      occupancy: s.occupancy_pct,
                      priceTrend: priceFit ? Math.max(0, priceFit.intercept + priceFit.slope * i) : null,
                      occTrend:
                        occFit && isComplete(s)
                          ? Math.min(100, Math.max(0, occFit.intercept + occFit.slope * i))
                          : null,
                    }));
                    // Selector order: keep default order but push 3GD to the far right.
                    const chartProps = [...monthlyStats.properties].sort(
                      (a, b) => (a.nickname === '3GD' ? 1 : 0) - (b.nickname === '3GD' ? 1 : 0)
                    );
                    return (
                      <Box sx={{ mt: 3 }}>
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2" sx={{ mr: 1 }}>History</Typography>
                          {chartProps.map((p) => (
                            <Chip
                              key={p.property_id}
                              size="small"
                              label={p.nickname}
                              color={p.property_id === selected.property_id ? 'primary' : 'default'}
                              variant={p.property_id === selected.property_id ? 'filled' : 'outlined'}
                              onClick={() => setStatsChartPropId(p.property_id)}
                            />
                          ))}
                        </Box>
                        {chartData.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No monthly history for {selected.nickname}.
                          </Typography>
                        ) : (
                          <Box sx={{ width: '100%', height: 280 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" minTickGap={24} />
                                <YAxis
                                  yAxisId="price"
                                  tick={{ fontSize: 10 }}
                                  tickFormatter={(v: number) => new Intl.NumberFormat(undefined, { notation: 'compact' }).format(v)}
                                />
                                <YAxis
                                  yAxisId="occ"
                                  orientation="right"
                                  domain={[0, 100]}
                                  tick={{ fontSize: 10 }}
                                  tickFormatter={(v: number) => `${v}%`}
                                />
                                <Tooltip
                                  formatter={(value: any, name: string) =>
                                    name.startsWith('Occupancy')
                                      ? [value == null ? '—' : `${Number(value).toFixed(0)}%`, name]
                                      : [value == null ? '—' : formatCurrency(Number(value)), name]
                                  }
                                />
                                <Legend />
                                <Bar yAxisId="occ" dataKey="occupancy" name="Occupancy" fill="#90caf9" barSize={10} />
                                <Line
                                  yAxisId="price"
                                  type="monotone"
                                  dataKey="price"
                                  name="Avg €/night"
                                  stroke="#c62828"
                                  strokeWidth={2}
                                  dot={{ r: 2 }}
                                  connectNulls={false}
                                />
                                <Line
                                  yAxisId="occ"
                                  type="linear"
                                  dataKey="occTrend"
                                  name="Occupancy trend"
                                  stroke="#1565c0"
                                  strokeWidth={2}
                                  strokeDasharray="6 4"
                                  dot={false}
                                  activeDot={false}
                                  connectNulls
                                  legendType="plainline"
                                />
                                <Line
                                  yAxisId="price"
                                  type="linear"
                                  dataKey="priceTrend"
                                  name="Avg €/night trend"
                                  stroke="#ef9a9a"
                                  strokeWidth={2}
                                  strokeDasharray="6 4"
                                  dot={false}
                                  activeDot={false}
                                  connectNulls
                                  legendType="plainline"
                                />
                              </ComposedChart>
                            </ResponsiveContainer>
                          </Box>
                        )}
                      </Box>
                    );
                  })()}
              </CollapsiblePanel>
            </Grid>

            <Grid size={{ xs: 12 }} sx={{ order: 2 }}>
              <CollapsiblePanel
                icon={<BarChartIcon color="primary" />}
                title="Stays Timeline — by Property"
                actions={
                  <Button size="small" href="/bookings">
                    Manage bookings
                  </Button>
                }
              >
                  {stayTimeline.hasPayoutScale && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary">Payout (relative to each property)</Typography>
                      <Typography variant="caption" color="text.secondary">Lower</Typography>
                      <Box
                        sx={{
                          width: 140,
                          height: 10,
                          borderRadius: 5,
                          background: 'linear-gradient(to right, rgb(21,101,192), rgb(198,40,40))',
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">Higher</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        (grey = no payout data)
                      </Typography>
                    </Box>
                  )}
                  {stayTimeline.count === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No stays in this window.
                    </Typography>
                  ) : (
                    <Box ref={timelineScrollRef} sx={{ width: '100%', overflowX: 'auto' }}>
                      <Box sx={{ width: 104 + stayTimeline.totalWidthPx }}>
                        {/* Month header */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-end', mb: 0.25 }}>
                          <Box sx={{ width: 104, flexShrink: 0, position: 'sticky', left: 0, zIndex: 3, bgcolor: 'background.paper' }} />
                          <Box sx={{ position: 'relative', width: stayTimeline.totalWidthPx, flexShrink: 0, display: 'flex' }}>
                            {stayTimeline.monthBands.map((mb, i) => (
                              <Box
                                key={`mon-${i}`}
                                sx={{
                                  width: mb.widthPx,
                                  flexShrink: 0,
                                  px: 0.5,
                                  fontSize: 10,
                                  fontWeight: 600,
                                  color: 'text.secondary',
                                  borderLeft: 1,
                                  borderColor: 'divider',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                }}
                              >
                                {mb.label}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                        {/* Weekday axis header */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-end', mb: 0.5 }}>
                          <Box sx={{ width: 104, flexShrink: 0, position: 'sticky', left: 0, zIndex: 3, bgcolor: 'background.paper' }} />
                          <Box sx={{ position: 'relative', width: stayTimeline.totalWidthPx, flexShrink: 0, display: 'flex' }}>
                            {stayTimeline.dayCells.map((c, i) => (
                              <Box
                                key={`day-${i}`}
                                sx={{
                                  width: stayTimeline.PX_PER_DAY,
                                  flexShrink: 0,
                                  textAlign: 'center',
                                  fontSize: 9,
                                  lineHeight: 1.3,
                                  color: c.isWeekend ? 'text.disabled' : 'text.secondary',
                                  borderLeft: i % 7 === 0 ? 1 : 0,
                                  borderColor: 'divider',
                                }}
                              >
                                <Box component="span" sx={{ display: 'block' }}>{c.letter}</Box>
                                <Box component="span" sx={{ display: 'block', fontSize: 8, opacity: 0.85 }}>
                                  {String(c.dom).padStart(2, '0')}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>

                        {/* One lane per property */}
                        {stayTimeline.lanes.map((lane) => (
                          <Box
                            key={lane.property}
                            sx={{ display: 'flex', alignItems: 'stretch', borderTop: 1, borderColor: 'divider' }}
                          >
                            <Box sx={{ width: 104, flexShrink: 0, pr: 1, py: 0.5, display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'sticky', left: 0, zIndex: 3, bgcolor: 'background.paper' }}>
                              <Typography variant="caption" noWrap sx={{ fontWeight: 600, display: 'block' }}>
                                {lane.property}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
                                {lane.stays.length} stay{lane.stays.length === 1 ? '' : 's'}
                              </Typography>
                            </Box>
                            <Box sx={{ position: 'relative', width: stayTimeline.totalWidthPx, flexShrink: 0, minHeight: 34 }}>
                              {/* Weekend shading */}
                              {stayTimeline.weekendBands.map((w, i) => (
                                <Box
                                  key={`we-${i}`}
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    bottom: 0,
                                    left: w.leftPx,
                                    width: w.widthPx,
                                    bgcolor: 'text.primary',
                                    opacity: 0.1,
                                  }}
                                />
                              ))}
                              {/* Week separators */}
                              {stayTimeline.weekLines.map((l, i) => (
                                <Box
                                  key={`wl-${i}`}
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    bottom: 0,
                                    left: l,
                                    borderLeft: 1,
                                    borderColor: 'divider',
                                  }}
                                />
                              ))}
                              {/* Today marker */}
                              {stayTimeline.todayPx != null && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    bottom: 0,
                                    left: stayTimeline.todayPx,
                                    borderLeft: '2px dashed',
                                    borderColor: 'text.secondary',
                                    zIndex: 2,
                                  }}
                                />
                              )}
                              {/* Stays for this property */}
                              {lane.stays.map((s) => (
                                <MuiTooltip
                                  key={s.id}
                                  arrow
                                  title={
                                    <Box sx={{ minWidth: 180 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{lane.property}</Typography>
                                      <Typography variant="body2" sx={{ mb: 0.5 }}>{s.guest}</Typography>
                                      <Typography variant="caption" component="div">
                                        {formatBookingDate(s.checkIn)} → {formatBookingDate(s.checkOut)}
                                      </Typography>
                                      <Typography variant="caption" component="div">
                                        {s.nights} night{s.nights === 1 ? '' : 's'}
                                        {s.guests > 0
                                          ? ` · ${s.guests} guest${s.guests === 1 ? '' : 's'}${s.children > 0 ? ` (${s.adults}A/${s.children}C)` : ''}`
                                          : ''}
                                      </Typography>
                                      {s.payout != null && (
                                        <Typography variant="caption" component="div">
                                          Payout: {formatCurrency(s.payout)}{s.currency ? ` ${s.currency}` : ''}
                                        </Typography>
                                      )}
                                      {(s.flightIn || s.flightOut) && (
                                        <Typography variant="caption" component="div">
                                          {s.flightIn ? `✈ In: ${s.flightIn}` : ''}
                                          {s.flightIn && s.flightOut ? ' · ' : ''}
                                          {s.flightOut ? `✈ Out: ${s.flightOut}` : ''}
                                        </Typography>
                                      )}
                                      <Typography variant="caption" component="div" sx={{ mt: 0.5, opacity: 0.85 }}>
                                        {s.source || 'Unknown source'}
                                        {s.status ? ` · ${s.status}` : ''}
                                        {s.mergedCount > 0 ? ` · ${s.mergedCount + 1} linked records` : ''}
                                      </Typography>
                                      <Typography variant="caption" component="div" sx={{ opacity: 0.85 }}>
                                        Booking #{s.id}
                                        {s.externalId ? ` · ext ${s.externalId.length > 18 ? s.externalId.slice(0, 18) + '…' : s.externalId}` : ''}
                                      </Typography>
                                      <Typography variant="caption" component="div" sx={{ mt: 0.5, fontStyle: 'italic', opacity: 0.7 }}>
                                        Click to open the booking
                                      </Typography>
                                    </Box>
                                  }
                                >
                                  <Box
                                    onClick={() => navigate(`/bookings/${s.id}`)}
                                    sx={{
                                      position: 'absolute',
                                      top: '18%',
                                      height: '64%',
                                      left: s.leftPx,
                                      width: s.widthPx,
                                      minWidth: 4,
                                      bgcolor: s.color,
                                      color: '#fff',
                                      borderRadius: 0.75,
                                      borderTopLeftRadius: s.clippedStart ? 0 : undefined,
                                      borderBottomLeftRadius: s.clippedStart ? 0 : undefined,
                                      borderTopRightRadius: s.clippedEnd ? 0 : undefined,
                                      borderBottomRightRadius: s.clippedEnd ? 0 : undefined,
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      px: 0.5,
                                      overflow: 'hidden',
                                      fontSize: 10,
                                      whiteSpace: 'nowrap',
                                      zIndex: 1,
                                      transition: 'filter 120ms ease',
                                      '&:hover': { filter: 'brightness(0.9)' },
                                    }}
                                  >
                                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {s.guest}
                                    </Box>
                                  </Box>
                                </MuiTooltip>
                              ))}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
              </CollapsiblePanel>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }} sx={{ order: 1, alignSelf: 'flex-start' }}>
              <CollapsiblePanel
                icon={<SyncIcon color="primary" />}
                title="Cron Jobs"
                actions={
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleSyncNow}
                    disabled={syncingNow}
                    sx={{ flexShrink: 0 }}
                  >
                    {syncingNow ? 'Syncing...' : 'Sync Now'}
                  </Button>
                }
              >
                  {allCronRuns.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                      {visibleCronRuns.map((run) => {
                        const e2eLine = getE2ePlaywrightLine(run.details);
                        const performanceDisplay = getPerformanceDisplay(run.details);
                        return (
                        <Accordion
                          key={run.id}
                          expanded={expandedRunId === run.id}
                          onChange={(_, expanded) => setExpandedRunId(expanded ? run.id : false)}
                          disableGutters
                          elevation={0}
                          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: 0.75,
                                width: '100%',
                                minWidth: 0,
                                pr: 0.5,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {formatDateTime(run.run_at)}
                                </Typography>
                                <Chip
                                  label={
                                    run.status === 'e2e_ok'
                                      ? 'E2E passed'
                                      : run.status === 'e2e_bad'
                                        ? 'E2E failed'
                                        : run.status
                                  }
                                  size="small"
                                  color={
                                    run.status === 'success' || run.status === 'e2e_ok'
                                      ? 'success'
                                      : run.status === 'error' || run.status === 'e2e_bad'
                                        ? 'error'
                                        : 'warning'
                                  }
                                />
                              </Box>
                              <Stack spacing={0.35} sx={{ width: '100%' }}>
                                {e2eLine.text ? <CronRunSummaryRow {...e2eLine} /> : null}
                                <CronRunSummaryRow {...getBackupSummaryLine(run.details?.backup)} />
                                <CronRunSummaryRow {...getRevolutLine(run.details?.revolut, 'transactions')} />
                                <CronRunSummaryRow {...getRevolutUpdatedLine(run.details?.revolut, 'transactions')} />
                                <CronRunSummaryRow {...getRevolutLine(run.details?.revolut, 'expenses')} />
                                <CronRunSummaryRow {...getRevolutUpdatedLine(run.details?.revolut, 'expenses')} />
                                <CronRunSummaryRow {...getBookingsSummaryLine(run.details?.bookings)} />
                                <CronRunSummaryRow
                                  failed={performanceDisplay.failed}
                                  text={performanceDisplay.summary}
                                  state={performanceDisplay.healthState}
                                />
                              </Stack>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 1.25 }}>
                            <Box sx={{ mb: 1.5 }}>
                              <Typography component="h4" variant="subtitle2" sx={{ ...cronDetailSectionHeaderSx, mt: 0 }}>
                                A) Cron job status
                              </Typography>
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Trigger type:{' '}
                                </Box>
                                {getCronTriggerTypeLabel(run.message)}
                              </Typography>
                              <Typography variant="caption" display="block" sx={{ mt: 0.35 }}>
                                <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Run Status:{' '}
                                </Box>
                                {getCronRunStatusLabel(run.message, String(run.status || ''))}
                              </Typography>
                              <Typography
                                variant="caption"
                                display="block"
                                sx={{ mt: 0.35 }}
                                color={
                                  run.details?.email_report?.sent
                                    ? 'success.main'
                                    : run.details?.email_report?.skipped
                                      ? 'warning.main'
                                      : run.details?.email_report
                                        ? 'error.main'
                                        : 'text.secondary'
                                }
                              >
                                <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Email confirmation:{' '}
                                </Box>
                                {getEmailConfirmationSummary(run.details?.email_report)}
                              </Typography>
                            </Box>
                            {run.details?.run_kind === 'playwright_e2e' &&
                            typeof run.details?.failure_snippet === 'string' &&
                            run.details.failure_snippet.trim() ? (
                              <Box sx={{ mb: 1.5 }}>
                                <Typography component="h4" variant="subtitle2" sx={cronDetailSectionHeaderSx}>
                                  E2E failure (truncated)
                                </Typography>
                                <Typography
                                  variant="caption"
                                  component="pre"
                                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.5 }}
                                >
                                  {run.details.failure_snippet}
                                </Typography>
                              </Box>
                            ) : null}
                            <Box sx={{ mb: 1.5 }}>
                              <Typography component="h4" variant="subtitle2" sx={cronDetailSectionHeaderSx}>
                                B) Performance
                              </Typography>
                              <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                                <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Total duration:{' '}
                                </Box>
                                {performanceDisplay.totalDuration}
                              </Typography>
                              <Typography variant="caption" display="block" sx={{ mt: 0.35, color: 'text.secondary' }}>
                                <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Stage timings:{' '}
                                </Box>
                                {performanceDisplay.stageTimings}
                              </Typography>
                              <Typography variant="caption" display="block" sx={{ mt: 0.35, color: 'text.secondary' }}>
                                <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  RSS start/end/delta:{' '}
                                </Box>
                                {performanceDisplay.rssTriplet}
                              </Typography>
                              <Typography variant="caption" display="block" sx={{ mt: 0.35, color: 'text.secondary' }}>
                                <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Peak memory:{' '}
                                </Box>
                                {performanceDisplay.peakMemory}
                              </Typography>
                              <Typography
                                variant="caption"
                                display="block"
                                sx={{ mt: 0.35 }}
                                color={
                                  performanceDisplay.healthState === 'critical'
                                    ? 'error.main'
                                    : performanceDisplay.healthState === 'warning'
                                      ? 'warning.main'
                                      : 'success.main'
                                }
                              >
                                <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Health:{' '}
                                </Box>
                                {performanceDisplay.healthLabel}
                              </Typography>
                              <Typography
                                variant="caption"
                                display="block"
                                sx={{ mt: 0.35 }}
                                color={
                                  performanceDisplay.healthState === 'critical'
                                    ? 'error.main'
                                    : performanceDisplay.healthState === 'warning'
                                      ? 'warning.main'
                                      : 'text.secondary'
                                }
                              >
                                <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Restart hint:{' '}
                                </Box>
                                {performanceDisplay.restartHint}
                              </Typography>
                            </Box>
                            <Box sx={{ mb: 1.5 }}>
                              <Typography component="h4" variant="subtitle2" sx={cronDetailSectionHeaderSx}>
                                C) DB Backup
                              </Typography>
                              <Typography
                                variant="caption"
                                display="block"
                                sx={{ mt: 0.5 }}
                                color={
                                  run.details?.backup?.success == null
                                    ? 'text.secondary'
                                    : run.details?.backup?.success
                                      ? 'success.main'
                                      : 'error.main'
                                }
                              >
                                <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  DB Backup Status:{' '}
                                </Box>
                                {run.details?.backup?.success == null
                                  ? 'n/a'
                                  : run.details.backup.success
                                    ? 'Success'
                                    : 'Failed'}
                              </Typography>
                              <Typography variant="caption" display="block" sx={{ mt: 0.35, color: 'text.secondary' }}>
                                <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  DB Type:{' '}
                                </Box>
                                {run.details?.backup?.database_type
                                  ? String(run.details.backup.database_type).toUpperCase()
                                  : 'n/a'}
                              </Typography>
                              <Typography variant="caption" display="block" sx={{ mt: 0.35, color: 'text.secondary' }}>
                                <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Backup file created:{' '}
                                </Box>
                                {run.details?.backup?.success == null
                                  ? 'n/a'
                                  : run.details.backup.success
                                    ? 'Yes'
                                    : 'No'}
                              </Typography>
                              <Typography variant="caption" display="block" sx={{ mt: 0.35, color: 'text.secondary' }}>
                                <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Backup File size:{' '}
                                </Box>
                                {formatBackupSize(run.details?.backup?.size_bytes) || 'n/a'}
                              </Typography>
                              {(() => {
                                const ru = run.details?.backup?.remote_upload;
                                const destinations = ru?.destinations || [];
                                const skipped = ru?.enabled === false || ru?.status === 'skipped';
                                if (destinations.length > 0) {
                                  return destinations.map((dest, idx) => {
                                    const label = dest.overwrite
                                      ? `${formatStorageProvider(dest.provider)} (latest, overwrite)`
                                      : formatStorageProvider(dest.provider);
                                    const ok = Boolean(dest.success);
                                    return (
                                      <Typography
                                        key={`${dest.name || dest.provider || idx}`}
                                        variant="caption"
                                        display="block"
                                        sx={{ mt: 0.35 }}
                                        color={ok ? 'success.main' : 'error.main'}
                                      >
                                        <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                          Uploaded to {label}:{' '}
                                        </Box>
                                        {ok ? 'Success' : 'Failed'}
                                        {dest.provider === 'dropbox' && (
                                          <>
                                            {' - '}
                                            <Link href={DROPBOX_DB_BACKUPS_FOLDER_URL} target="_blank" rel="noopener noreferrer">
                                              Open backups folder
                                            </Link>
                                          </>
                                        )}
                                        {dest.error ? ` — ${dest.error}` : ''}
                                      </Typography>
                                    );
                                  });
                                }
                                return (
                                  <>
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      sx={{ mt: 0.35 }}
                                      color={skipped ? 'text.secondary' : ru?.success ? 'success.main' : 'error.main'}
                                    >
                                      <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                        Uploaded to {formatStorageProvider(ru?.provider)}:{' '}
                                      </Box>
                                      {skipped ? 'Skipped' : ru?.success ? 'Success' : 'Failed'}
                                      {!skipped && ru?.provider === 'dropbox' && (
                                        <>
                                          {' - '}
                                          <Link href={DROPBOX_DB_BACKUPS_FOLDER_URL} target="_blank" rel="noopener noreferrer">
                                            Open backups folder
                                          </Link>
                                        </>
                                      )}
                                    </Typography>
                                    {(ru?.error || ru?.reason) && (
                                      <Typography
                                        variant="caption"
                                        display="block"
                                        color={skipped ? 'text.secondary' : 'error.main'}
                                        sx={{ mt: 0.5 }}
                                      >
                                        {ru?.error || ru?.reason}
                                      </Typography>
                                    )}
                                  </>
                                );
                              })()}
                            </Box>
                            {renderRunSection(
                              'C) Transactions (IRE)',
                              getItems(run.details?.revolut?.transactions?.ireland),
                              'transaction',
                              (item) => getRunItemHref('transaction', item),
                              'View in Revolut',
                              'IE Transaction'
                            )}
                            {renderRunSection(
                              'D) Transactions (UK)',
                              getItems(run.details?.revolut?.transactions?.uk),
                              'transaction',
                              (item) => getRunItemHref('transaction', item),
                              'View in Revolut',
                              'UK Transaction'
                            )}
                            {renderRunSection(
                              'E) Expenses (IRE)',
                              getItems(run.details?.revolut?.expenses?.ireland),
                              'expense',
                              (item) => getRunItemHref('expense', item),
                              'View in Revolut',
                              'IE Expense'
                            )}
                            {renderRunSection(
                              'F) Expenses (UK)',
                              getItems(run.details?.revolut?.expenses?.uk),
                              'expense',
                              (item) => getRunItemHref('expense', item),
                              'View in Revolut',
                              'UK Expense'
                            )}
                            {renderRunSection(
                              'G) Bookings',
                              getNewOrChangedBookingItems(run.details?.bookings?.items),
                              'booking',
                              (item) => getRunItemHref('booking', item),
                              'View in Bookings',
                              'Booking'
                            )}
                            {(run.details?.bookings?.errors?.length || 0) > 0 && (
                              <Box sx={{ mb: 1.5 }}>
                                <Typography component="h4" variant="subtitle2" sx={cronDetailSectionHeaderSx}>
                                  H) Booking import errors
                                </Typography>
                                <Typography variant="caption" display="block" color="error.main">
                                  {run.details?.bookings?.errors?.join(' | ')}
                                </Typography>
                              </Box>
                            )}
                            {(() => {
                              const syncIssueRows = [
                                {
                                  label: 'IE transaction sync',
                                  error: getSyncError(run.details?.revolut?.transactions?.ireland),
                                },
                                {
                                  label: 'UK transaction sync',
                                  error: getSyncError(run.details?.revolut?.transactions?.uk),
                                },
                                {
                                  label: 'IE expense sync',
                                  error: getSyncError(run.details?.revolut?.expenses?.ireland),
                                },
                                {
                                  label: 'UK expense sync',
                                  error: getSyncError(run.details?.revolut?.expenses?.uk),
                                },
                              ].filter((entry) => !!entry.error);
                              if (syncIssueRows.length === 0) return null;
                              return (
                                <Box sx={{ mb: 0.5 }}>
                                  <Typography component="h4" variant="subtitle2" sx={cronDetailSectionHeaderSx}>
                                    I) Revolut sync issues
                                  </Typography>
                                  {syncIssueRows.map((entry) => (
                                    <Typography key={entry.label} variant="caption" display="block" color="error.main">
                                      {entry.label}: {entry.error}
                                    </Typography>
                                  ))}
                                </Box>
                              );
                            })()}
                          </AccordionDetails>
                        </Accordion>
                        );
                      })}
                      {canToggleCronRuns && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
                          <Button size="small" variant="text" onClick={toggleCronRunsView}>
                            {showMoreCronRuns ? 'View less' : `View more (${Math.min(allCronRuns.length, EXPANDED_CRON_RUNS_COUNT)})`}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No cron history available yet.
                    </Typography>
                  )}
              </CollapsiblePanel>
            </Grid>
          </Grid>
        )}
      </Box>
      <Dialog open={detailModalOpen} onClose={() => setDetailModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{detailModalTitle}</DialogTitle>
        <DialogContent dividers>
          {detailModalLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          )}

          {!detailModalLoading && detailModalTitle === 'Booking' && (
            <Box
              component="pre"
              sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.8rem' }}
            >
              {JSON.stringify(detailModalItem || {}, null, 2)}
            </Box>
          )}

          {!detailModalLoading && detailModalTitle.includes('Expense') && (
            <>
              {detailModalResolved ? (
                <Stack spacing={1.25}>
                  <Typography variant="body2">
                    <strong>Merchant / payer</strong>:{' '}
                    {detailModalResolved.merchant || detailModalResolved.payer || '—'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Amount</strong>:{' '}
                    {formatMoney(detailModalResolved.amount?.value, detailModalResolved.amount?.currency)}
                  </Typography>
                  {(detailModalResolved.spent_amount?.value != null ||
                    detailModalResolved.spent_amount?.currency) && (
                    <Typography variant="body2">
                      <strong>Spent</strong>:{' '}
                      {formatMoney(
                        detailModalResolved.spent_amount?.value,
                        detailModalResolved.spent_amount?.currency
                      )}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    <strong>Category</strong>:{' '}
                    {[detailModalResolved.category?.name, detailModalResolved.category?.code]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>State</strong>: {detailModalResolved.state ?? '—'}
                    {detailModalResolved.transaction_type ? ` · ${detailModalResolved.transaction_type}` : ''}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Expense date</strong>:{' '}
                    {detailModalResolved.expense_date
                      ? formatDateTime(String(detailModalResolved.expense_date))
                      : '—'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Account</strong>: {detailModalResolved.account_source ?? '—'}
                  </Typography>
                  {detailModalResolved.description ? (
                    <Typography variant="body2">
                      <strong>Description</strong>: {detailModalResolved.description}
                    </Typography>
                  ) : null}
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Revolut ID: {detailModalResolved.revolut_id}
                  </Typography>
                  <Link href="/revolut?tab=expenses" underline="hover">
                    Open Revolut Expenses tab
                  </Link>
                </Stack>
              ) : (
                <>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This expense was not found in the app database (or the request failed). Showing the cron
                    snapshot below.
                  </Alert>
                  <Box
                    component="pre"
                    sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.8rem' }}
                  >
                    {JSON.stringify(detailModalItem || {}, null, 2)}
                  </Box>
                </>
              )}
            </>
          )}

          {!detailModalLoading &&
            detailModalTitle.includes('Transaction') &&
            !detailModalTitle.includes('Expense') && (
              <>
                {detailModalResolved ? (
                  <Stack spacing={1.25}>
                    <Typography variant="body2">
                      <strong>Description</strong>: {detailModalResolved.description || '—'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Amount</strong>:{' '}
                      {formatMoney(
                        detailModalResolved.amount_display ?? detailModalResolved.amount,
                        detailModalResolved.currency
                      )}
                    </Typography>
                    {detailModalResolved.balance != null || detailModalResolved.balance_display != null ? (
                      <Typography variant="body2">
                        <strong>Balance after</strong>:{' '}
                        {formatMoney(
                          detailModalResolved.balance_display ?? detailModalResolved.balance,
                          detailModalResolved.currency
                        )}
                      </Typography>
                    ) : null}
                    <Typography variant="body2">
                      <strong>Merchant</strong>:{' '}
                      {effectiveMerchantName(detailModalResolved) || detailModalResolved.merchant_name || '—'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>State</strong>: {detailModalResolved.state ?? '—'} · <strong>Type</strong>:{' '}
                      {detailModalResolved.type ?? '—'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Completed</strong>:{' '}
                      {detailModalResolved.completed_at
                        ? formatDateTime(String(detailModalResolved.completed_at))
                        : '—'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Account</strong>: {detailModalResolved.account_source ?? '—'}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="text.secondary" display="block">
                      Revolut ID: {detailModalResolved.revolut_id}
                    </Typography>
                    <Link href="/revolut?tab=transactions" underline="hover">
                      Open Revolut Transactions tab
                    </Link>
                  </Stack>
                ) : (
                  <>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      This transaction was not found in the app database (or the request failed). Showing the
                      cron snapshot below.
                    </Alert>
                    <Box
                      component="pre"
                      sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.8rem' }}
                    >
                      {JSON.stringify(detailModalItem || {}, null, 2)}
                    </Box>
                  </>
                )}
              </>
            )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};